import { useState, useEffect, useMemo } from 'react';
import { Select, Table, Tag, Space, Spin, Empty, Typography, Tooltip, Row, Col, Input, Button, Modal, Form, message, Popconfirm, Menu, Descriptions } from 'antd';
import { FileTextOutlined, PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, CopyOutlined, CheckOutlined, ApiOutlined, BankOutlined, BookOutlined, BarChartOutlined, SettingOutlined, BellOutlined, RightOutlined, CodeOutlined } from '@ant-design/icons';
import ReactJson from '@microlink/react-json-view';
import { metaApi, testApi } from '../services/api';
import { buildChannelOptions, getChannelLogo, getCurrencyIcon } from '../utils/channelLogos';
import { useLanguage } from '../contexts/LanguageContext';
import { fieldLabelMap, fieldTipMap } from '../utils/apiDocI18n';

const { Text } = Typography;

// ─── Color tokens (aligned with Admin Console design language) ───
const COLORS = {
  bg: '#0a0a0f',
  bgCard: '#12121a',
  bgCode: '#1a1a2e',
  bgSidebar: '#ffffff',
  bgContent: '#f5f7fa',
  border: '#e2e8f0',
  borderLight: '#f0f2f5',
  accent: '#667eea',
  accentLight: '#764ba2',
  accentBg: '#f0f4ff',
  accentGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  green: '#22c55e',
  greenBg: '#ecfdf5',
  greenBorder: '#bbf7d0',
  blue: '#3b82f6',
  blueBg: '#eff6ff',
  blueBorder: '#bfdbfe',
  orange: '#f59e0b',
  orangeBg: '#fffbeb',
  red: '#ef4444',
  redBg: '#fef2f2',
  textPrimary: '#262626',
  textSecondary: '#64748b',
  textMuted: '#94a3b8',
  textCode: '#e2e8f0',
  codeBg: '#1e293b',
  codeHeaderBg: '#0f172a',
};

const getApiCodeLabels = (t) => ({
  ACCOUNT_CREATE: { label: t('doc_apiAccountCreate'), color: 'blue', icon: '👤', method: 'POST', uri: '/v1/fiat/payment/account/create', desc: t('doc_descAccountCreate') },
  ACCOUNT_CLOSE: { label: t('doc_apiAccountClose'), color: 'default', icon: '🚪', method: 'POST', uri: '/v1/fiat/payment/account/close', desc: t('doc_descAccountClose') },
  ACCOUNT_QUERY: { label: t('doc_apiAccountQuery'), color: 'cyan', icon: '🔍', method: 'GET', uri: '/v1/fiat/payment/account/query', desc: t('doc_descAccountQuery') },
  CARD_BIND: { label: t('doc_apiCardBind'), color: 'green', icon: '💳', method: 'POST', uri: '/v1/fiat/payment/bankaccount/bind', desc: t('doc_descCardBind') },
  CARD_QUERY: { label: t('doc_apiCardQuery'), color: 'lime', icon: '🔎', method: 'GET', uri: '/v1/fiat/payment/bankaccount/query', desc: t('doc_descCardQuery') },
  CARD_UNBIND: { label: t('doc_apiCardUnbind'), color: 'orange', icon: '✂️', method: 'POST', uri: '/v1/fiat/payment/bankaccount/unbind', desc: t('doc_descCardUnbind') },
  DEPOSIT_CREATE: { label: t('doc_apiDepositCreate'), color: 'gold', icon: '💰', method: 'POST', uri: '/v1/fiat/payment/deposit/create', desc: t('doc_descDepositCreate') },
  DEPOSIT_QUERY: { label: t('doc_apiDepositQuery'), color: 'geekblue', icon: '📋', method: 'GET', uri: '/v1/fiat/payment/deposit/query', desc: t('doc_descDepositQuery') },
  DEPOSIT_CLOSE: { label: t('doc_apiDepositClose'), color: 'orange', icon: '🚫', method: 'POST', uri: '/v1/fiat/payment/deposit/close', desc: t('doc_descDepositClose') },
  REFUND_CREATE: { label: t('doc_apiRefundCreate'), color: 'magenta', icon: '↩️', method: 'POST', uri: '/v1/fiat/payment/refund/create', desc: t('doc_descRefundCreate') },
  REFUND_QUERY: { label: t('doc_apiRefundQuery'), color: 'purple', icon: '📑', method: 'GET', uri: '/v1/fiat/payment/refund/query', desc: t('doc_descRefundQuery') },
  WITHDRAW_CREATE: { label: t('doc_apiWithdrawCreate'), color: 'red', icon: '🏦', method: 'POST', uri: '/v1/fiat/payment/withdraw/create', desc: t('doc_descWithdrawCreate') },
  WITHDRAW_QUERY: { label: t('doc_apiWithdrawQuery'), color: 'volcano', icon: '📄', method: 'GET', uri: '/v1/fiat/payment/withdraw/query', desc: t('doc_descWithdrawQuery') },
  CHANNEL_CONFIG_QUERY: { label: t('doc_apiChannelConfig'), color: 'blue', icon: '⚙️', method: 'GET', uri: '/v1/fiat/payment/channel/config/query', desc: t('doc_descChannelConfig') },
  ORDER_PAGE: { label: t('doc_apiOrderPage'), color: 'cyan', icon: '📊', method: 'POST', uri: '/v1/fiat/payment/order/page', desc: t('doc_descOrderPage') },
  BANK_LIST: { label: t('doc_apiBankList'), color: 'green', icon: '🏛️', method: 'GET', uri: '/v1/fiat/payment/bank/active/list', desc: t('doc_descBankList') },
  FILE_UPLOAD: { label: t('doc_apiFileUpload'), color: 'gold', icon: '📤', method: 'POST', uri: '/v1/fiat/payment/channel/files/upload', desc: t('doc_descFileUpload') },
  FILE_STATUS_QUERY: { label: t('doc_apiFileStatusQuery'), color: 'lime', icon: '📁', method: 'POST', uri: '/v1/fiat/payment/channel/files/batch-query', desc: t('doc_descFileStatusQuery') },
  USER_CREATE: { label: t('doc_apiUserCreate'), color: 'blue', icon: '👥', method: 'POST', uri: '/v1/fiat/payment/user/create', desc: t('doc_descUserCreate') },
  USER_QUERY: { label: t('doc_apiUserQuery'), color: 'cyan', icon: '🧑', method: 'GET', uri: '/v1/fiat/payment/user/query', desc: t('doc_descUserQuery') },
  BALANCE_QUERY: { label: t('doc_apiBalanceQuery'), color: 'green', icon: '💵', method: 'POST', uri: '/v1/fiat/payment/channel/balance/query', desc: t('doc_descBalanceQuery') },
  RATE_QUERY: { label: t('doc_apiRateQuery'), color: 'gold', icon: '💱', method: 'POST', uri: '/v1/fiat/payment/channel/rate/query', desc: t('doc_descRateQuery') },
  PAYOUT_REQUIREMENTS: { label: t('doc_apiPayoutRequirements'), color: 'purple', icon: '📋', method: 'GET', uri: '/v1/fiat/payment/bankaccount/payout/requirements', desc: t('doc_descPayoutRequirements') },
});

// 各场景的基础参数（所有渠道通用，请求体骨架）
const BASE_PARAMS = {
  ACCOUNT_CREATE: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'currency', label: '币种', type: 'STRING', required: true, tip: 'EQUALS_MONEY仅USD; LEAN_TECH仅AED; CVPAY仅VND; 其他渠道需checkSupportCurrency' },
    { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: true, tip: 'EQUALS_MONEY / PAGSMILE / SGB / PYVIO / LEAN_TECH / KORAPAY / PALMPAY / BANK_FRICK / BANKING_CIRCLE / INSWITCH / CVPAY' },
    { name: 'paymentMethod', label: '支付方式', type: 'ENUM', required: true, tip: 'LEAN_TECH仅LEAN; BANKING_CIRCLE仅VIRTUAL_ACCOUNT; INSWITCH仅VIRTUAL_ACCOUNT(BRL); CVPAY仅BANK_TRANSFER; 其他: VIRTUAL_ACCOUNT / PIX / VIETQR / ZEN_WALLET / INTERACE_REQUEST / LEAN / BANK_TRANSFER' },
    { name: 'userType', label: '用户类型', type: 'ENUM', required: true, tip: 'LEAN_TECH仅INDIVIDUAL; 其他: INDIVIDUAL / BUSINESS / ADMINISTRATOR / ALL' },
    { name: 'extParam', label: '扩展参数', type: 'JSON_STRING', required: false, tip: 'CVPAY无需传入; 其他渠道均需传入，JSON格式字符串，见下方渠道差异字段' },
  ],
  CARD_BIND: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'accountId', label: '账户ID', type: 'STRING', required: false, tip: 'BANK_FRICK/PYVIO外所有渠道必填' },
    { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: true, tip: 'EQUALS_MONEY / PAGSMILE / SGB / PYVIO / LEAN_TECH / KORAPAY / PALMPAY / BANK_FRICK / BANKING_CIRCLE / INSWITCH / TAZAPAY / CVPAY' },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'accountName', label: '账户姓名', type: 'STRING', required: true, maxLen: 64 },
    { name: 'userType', label: '用户类型', type: 'ENUM', required: true, tip: 'INDIVIDUAL / BUSINESS / ADMINISTRATOR / ALL' },
    { name: 'bic', label: '银行编码', type: 'STRING', required: false, maxLen: 16, tip: 'EQUALS_MONEY/SGB/LEAN_TECH/KORAPAY/CVPAY必填; BANK_FRICK仅SWIFT必填; PYVIO仅USD必填; TAZAPAY按走廊动态要求' },
    { name: 'accountNumber', label: '卡号', type: 'STRING', required: false, maxLen: 64, tip: 'EQUALS_MONEY/SGB/LEAN_TECH/KORAPAY/PYVIO/CVPAY必填; BANK_FRICK/BANKING_CIRCLE仅bindType=BANKACCOUNT必填; TAZAPAY bindType=BANKACCOUNT时必填' },
    { name: 'bankName', label: '银行名字', type: 'STRING', required: true, maxLen: 64, tip: 'BANKING_CIRCLE仅bindType=BANKACCOUNT必填，其余渠道均必填' },
    { name: 'iban', label: 'IBAN', type: 'STRING', required: false, minLen: 15, maxLen: 34, tip: '@Size(min=15,max=34); LEAN_TECH必填; BANK_FRICK/BANKING_CIRCLE仅bindType=IBAN必填; TAZAPAY bindType=IBAN时必填' },
    { name: 'bankCountry', label: '银行国家', type: 'STRING', required: false, maxLen: 64, tip: 'EQUALS_MONEY/SGB必填(2位); LEAN_TECH必填(3位); PYVIO/BANKING_CIRCLE/TAZAPAY必填(2位)' },
    { name: 'currency', label: '币种', type: 'STRING', required: true, maxLen: 16 },
    { name: 'bindType', label: '账户类型', type: 'STRING', required: false, tip: 'BANK_FRICK/BANKING_CIRCLE必填: IBAN / BANKACCOUNT; TAZAPAY支持: BANKACCOUNT / IBAN / LOCAL_PAYMENT' },
    { name: 'extParam', label: '扩展参数', type: 'JSON_STRING', required: false, tip: 'EQUALS_MONEY/LEAN_TECH/BANK_FRICK/BANKING_CIRCLE/PYVIO/TAZAPAY/CVPAY必填，JSON格式字符串，见下方' },
  ],
  DEPOSIT_CREATE: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'externalOrderId', label: '外部订单号', type: 'STRING', required: true, maxLen: 256, tip: '幂等key' },
    { name: 'accountId', label: '账户ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'currency', label: '币种', type: 'ENUM', required: true, tip: 'CurrencyEnum校验: USD / RUB / UAH / GBP / EUR / DKK / BRL / MXN / ARS / COP / AED / NGN / HKD / SGD / VND / ALL' },
    { name: 'amount', label: '金额', type: 'DECIMAL', required: true, tip: '正数，最大20位整数+2位小数; CVPAY最低100000' },
    { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: true, tip: 'EQUALS_MONEY / PAGSMILE / SGB / PYVIO / LEAN_TECH / KORAPAY / PALMPAY / BANK_FRICK / BANKING_CIRCLE / INSWITCH / CVPAY' },
    { name: 'depositMethod', label: '充值方式', type: 'ENUM', required: true, tip: 'VIRTUAL_ACCOUNT / PIX / VIETQR / ZEN_WALLET / INTERACE_REQUEST / LEAN / BANK_TRANSFER; CVPAY仅BANK_TRANSFER' },
    { name: 'callbackUrl', label: '回调地址', type: 'STRING', required: true, maxLen: 2048, tip: 'StrictCallbackUrl校验，allowHttp=true，不可为空' },
    { name: 'extParam', label: '扩展参数', type: 'JSON_STRING', required: false, tip: '各渠道差异字段，JSON格式字符串，见下方; CVPAY必填(buyerName/buyerPhone/buyerEmail/buyerIDType/buyerID)' },
  ],
  DEPOSIT_CLOSE: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'externalOrderId', label: '外部订单号', type: 'STRING', required: false, maxLen: 64, tip: '与orderId至少传一个' },
    { name: 'orderId', label: '订单号', type: 'STRING', required: false, maxLen: 64, tip: '与externalOrderId至少传一个' },
  ],
  WITHDRAW_CREATE: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'externalOrderId', label: '外部订单号', type: 'STRING', required: true, maxLen: 256, tip: '幂等key' },
    { name: 'currency', label: '币种', type: 'STRING', required: true, maxLen: 16, tip: 'EQUALS_MONEY仅USD; LEAN_TECH仅AED; INSWITCH仅BRL; CVPAY仅VND; SGB需checkSupportCurrency; TAZAPAY为holdingCurrency(扣款币种); 其他渠道无特殊限制' },
    { name: 'amount', label: '金额', type: 'DECIMAL', required: true, tip: '正数，AmountValid校验; KORAPAY最低1000; CVPAY最低50000' },
    { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: true, tip: 'EQUALS_MONEY / SGB / PYVIO / LEAN_TECH / KORAPAY / BANK_FRICK / BANKING_CIRCLE / INSWITCH / TAZAPAY / CVPAY' },
    { name: 'withdrawMethod', label: '提现方式', type: 'ENUM', required: true, tip: 'BANK_TRANSFER / PIX_CODE / INTERAC / EFT; INSWITCH仅支持PIX_CODE; TAZAPAY仅支持BANK_TRANSFER; CVPAY仅支持BANK_TRANSFER' },
    { name: 'callbackUrl', label: '回调地址', type: 'STRING', required: true, maxLen: 2048, tip: '必须以http://或https://开头' },
    { name: 'extParam', label: '扩展参数', type: 'JSON_STRING', required: true, tip: '所有渠道均需传入，JSON格式字符串，见下方; CVPAY需传recipientId' },
  ],
  ACCOUNT_CLOSE: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'accountId', label: '账户ID', type: 'STRING', required: false, maxLen: 64, tip: 'DTO无@NotBlank，但业务逻辑需要' },
    { name: 'reason', label: '关户原因', type: 'STRING', required: false },
  ],
  ACCOUNT_QUERY: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'accountId', label: '账户ID', type: 'STRING', required: true },
  ],
  CARD_QUERY: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'recipientId', label: '绑卡ID', type: 'STRING', required: true },
  ],
  CARD_UNBIND: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'recipientId', label: '绑卡ID', type: 'STRING', required: true },
    { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: true, tip: 'EQUALS_MONEY / PAGSMILE / SGB / PYVIO / LEAN_TECH / KORAPAY / PALMPAY / BANK_FRICK / BANKING_CIRCLE / INSWITCH / CVPAY' },
  ],
  DEPOSIT_QUERY: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'externalOrderId', label: '外部订单号', type: 'STRING', required: false, tip: '与orderId至少传一个' },
    { name: 'orderId', label: '订单号', type: 'STRING', required: false, tip: '与externalOrderId至少传一个' },
  ],
  REFUND_CREATE: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'externalOrderId', label: '外部退款订单号', type: 'STRING', required: true, maxLen: 256 },
    { name: 'depositOrderId', label: '充值订单号', type: 'STRING', required: true },
    { name: 'callbackUrl', label: '回调地址', type: 'STRING', required: true, maxLen: 2048, tip: 'StrictCallbackUrl校验，allowHttp=true，不可为空' },
    { name: 'description', label: '退款描述', type: 'STRING', required: false },
    { name: 'amount', label: '退款金额', type: 'DECIMAL', required: false, tip: '不传则全额退款' },
    { name: 'extParam', label: '扩展参数', type: 'JSON_STRING', required: false },
  ],
  REFUND_QUERY: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'externalOrderId', label: '外部退款订单号', type: 'STRING', required: false, tip: '与orderId至少传一个' },
    { name: 'orderId', label: '退款单号', type: 'STRING', required: false, tip: '与externalOrderId至少传一个' },
  ],
  WITHDRAW_QUERY: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'externalOrderId', label: '外部订单号', type: 'STRING', required: false, tip: '与orderId至少传一个' },
    { name: 'orderId', label: '订单号', type: 'STRING', required: false, tip: '与externalOrderId至少传一个' },
  ],
  CHANNEL_CONFIG_QUERY: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'channelCode', label: '渠道代码', type: 'STRING', required: true },
    { name: 'currency', label: '币种', type: 'STRING', required: false },
  ],
  ORDER_PAGE: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'channelCode', label: '渠道代码', type: 'STRING', required: true },
    { name: 'filters.currency', label: '币种', type: 'STRING', required: true },
    { name: 'filters.orderType', label: '交易类型', type: 'STRING', required: true, tip: 'DEPOSIT / WITHDRAW / REFUND' },
    { name: 'filters.orderStatusList', label: '订单状态', type: 'ARRAY', required: false, tip: '如 ["SUCCESS","FAILED"]' },
    { name: 'filters.externalUserId', label: '外部用户ID', type: 'STRING', required: false },
    { name: 'timeRange.startTime', label: '开始时间', type: 'LONG', required: true, tip: '毫秒时间戳，最大支持一周' },
    { name: 'timeRange.endTime', label: '结束时间', type: 'LONG', required: true, tip: '毫秒时间戳' },
    { name: 'pagination.page', label: '页码', type: 'INTEGER', required: true, tip: '最小值1' },
    { name: 'pagination.pageSize', label: '每页条数', type: 'INTEGER', required: true, tip: '1~100' },
  ],
  BANK_LIST: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'channelCode', label: '渠道代码', type: 'STRING', required: true },
  ],
  FILE_UPLOAD: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true },
    { name: 'channelCode', label: '渠道代码', type: 'STRING', required: true },
    { name: 'scene', label: '业务场景', type: 'STRING', required: true, tip: 'ACCOUNT_OPEN / DEPOSIT / WITHDRAW / CARD_BIND / REFUND' },
    { name: 'documentType', label: '文件类型', type: 'STRING', required: true, tip: 'ID_CARD / PASSPORT / DRIVER_LICENSE / SELFIE / ADDRESS_PROOF 等' },
    { name: 'bizId', label: '业务ID', type: 'STRING', required: true, tip: 'accountId / depositId / withdrawId' },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true },
    { name: 'file', label: '文件', type: 'FILE', required: true, tip: 'jpg/png/pdf，最大10MB' },
    { name: 'documentCountry', label: '文件国家', type: 'STRING', required: false },
    { name: 'documentSide', label: '文件面', type: 'STRING', required: false, tip: 'FRONT / BACK' },
  ],
  FILE_STATUS_QUERY: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true },
    { name: 'fileIds', label: '文件ID列表', type: 'ARRAY', required: true, tip: '@NotEmpty, @Size(max=50)' },
  ],
  USER_CREATE: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'operateType', label: '操作类型', type: 'STRING', required: false, tip: 'CREATE / UPDATE' },
    { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: true, tip: 'PYVIO / TAZAPAY' },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'userType', label: '用户类型', type: 'STRING', required: false, tip: 'INDIVIDUAL / BUSINESS; TAZAPAY仅支持BUSINESS' },
    { name: 'extParam', label: '扩展参数', type: 'JSON_STRING', required: false, tip: 'PYVIO/TAZAPAY渠道必填，JSON格式字符串，见下方渠道差异字段' },
  ],
  USER_QUERY: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'userId', label: '用户ID', type: 'STRING', required: false },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: false },
  ],
  BALANCE_QUERY: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: false },
    { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: true, tip: 'EQUALS_MONEY / PAGSMILE / SGB / PYVIO / LEAN_TECH / KORAPAY / PALMPAY / BANK_FRICK / BANKING_CIRCLE / INSWITCH / CVPAY' },
    { name: 'currency', label: '币种', type: 'STRING', required: true, tip: 'Service层checkParams校验' },
    { name: 'channelAccountNo', label: '渠道账户号', type: 'STRING', required: false, tip: '渠道有多个账号时可指定' },
  ],
  RATE_QUERY: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: true, tip: 'PYVIO / TAZAPAY' },
    { name: 'originCurrency', label: '原始币种', type: 'STRING', required: true },
    { name: 'targetCurrency', label: '目标币种', type: 'STRING', required: true },
    { name: 'payeeType', label: '收款人类型', type: 'STRING', required: false, tip: 'PYVIO必填: BENEFICIARY / SUPPLIER; TAZAPAY必填: local / swift / local_payment_network / wallet / tazapay_account' },
    { name: 'userId', label: '用户ID', type: 'STRING', required: false, tip: 'PYVIO: payeeType=BENEFICIARY时必填' },
    { name: 'destinationCountry', label: '目标国家', type: 'STRING', required: false, tip: 'TAZAPAY必填: ISO 3166-1 alpha-2 国家码，如 NG、DE、IN' },
    { name: 'amount', label: '出金金额(分)', type: 'LONG', required: false, tip: 'TAZAPAY必填: 单位为分，如 10000 表示 100.00' },
    { name: 'localPaymentNetworkType', label: '本地支付网络类型', type: 'STRING', required: false, tip: 'TAZAPAY: payeeType=local_payment_network 时必填，如 pix_brl' },
    { name: 'fundTransferNetwork', label: '资金转账网络', type: 'STRING', required: false, tip: 'TAZAPAY: payeeType=local 时可选，如 sepa / fedach / fedwire' },
  ],
  PAYOUT_REQUIREMENTS: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: true, tip: 'TAZAPAY' },
    { name: 'country', label: '目标国家', type: 'STRING', required: true, tip: 'ISO 3166-1 alpha-2，如 US / IN / HK / GB' },
    { name: 'currency', label: '目标币种', type: 'STRING', required: true, tip: 'ISO 4217，如 USD / INR / HKD / GBP' },
  ],
};

// 各接口的响应参数定义（data 部分）
const RESPONSE_PARAMS = {
  ACCOUNT_CREATE: [
    { name: 'accountId', label: '账户ID', type: 'STRING', required: true },
    { name: 'status', label: '开户状态', type: 'STRING', required: true, tip: 'PROCESSING / ACTIVE / FAILED / CLOSED / DENIED' },
  ],
  ACCOUNT_CLOSE: [
    { name: 'data', label: '结果', type: 'BOOLEAN', required: true, tip: 'true 表示关户成功' },
  ],
  ACCOUNT_QUERY: [
    { name: 'accountId', label: '账户ID', type: 'STRING', required: true },
    { name: 'status', label: '开户状态', type: 'STRING', required: true, tip: 'PROCESSING / ACTIVE / FAILED / CLOSED / DENIED' },
    { name: 'subStatus', label: '开户子状态', type: 'STRING', required: true, tip: 'INIT / PRE_CHECKING / CREATING_CHANNEL / WAITING_CONNECT / WAITING_ENTITY_CALLBACK / WAITING_CREATE_BENEFICIARY / AWAITING_BENEFICIARY_COOL_OFF / SUCCESS / FAILED' },
    { name: 'externalUserId', label: '业务线用户ID', type: 'STRING', required: true },
    { name: 'channelCode', label: '开户渠道', type: 'STRING', required: true },
    { name: 'paymentMethod', label: '支付方式', type: 'STRING', required: true },
    { name: 'currency', label: '开户币种', type: 'STRING', required: true },
    { name: 'redirectUrl', label: '跳转链接', type: 'STRING', required: false, tip: '开户需要跳转外部链接时返回' },
    { name: 'errorCode', label: '错误码', type: 'STRING', required: false, tip: '开户失败时返回' },
    { name: 'errorMsg', label: '错误信息', type: 'STRING', required: false, tip: '开户失败时返回' },
    { name: 'createTime', label: '创建时间', type: 'LONG', required: true },
    { name: 'updateTime', label: '最后更新时间', type: 'LONG', required: true },
    { name: 'accountDetail', label: '账户详情', type: 'OBJECT', required: false, tip: '开户成功时返回', children: [
      { name: 'accountName', label: '账户名', type: 'STRING', required: false },
      { name: 'accountNumber', label: '账户号码', type: 'STRING', required: true, tip: 'IBAN 或 accountNumber' },
      { name: 'bic', label: 'SWIFT Code', type: 'STRING', required: false, tip: 'Bank Identifier Code (BIC)' },
      { name: 'bankName', label: '银行名称', type: 'STRING', required: false },
      { name: 'bankCountry', label: '银行所在国家', type: 'STRING', required: false },
      { name: 'bankAddress', label: '银行地址', type: 'STRING', required: false },
    ] },
    { name: 'userType', label: '用户类型', type: 'STRING', required: true, tip: 'INDIVIDUAL / BUSINESS' },
    { name: 'extParam', label: '扩展参数', type: 'JSON_STRING', required: false, tip: '根据渠道返回不同字段', children: [
      { name: 'customerId', label: '渠道用户ID', type: 'STRING', required: true, _channel: 'LEAN_TECH' },
      { name: 'accessToken', label: 'Access Token', type: 'STRING', required: true, _channel: 'LEAN_TECH' },
      { name: 'channelAppToken', label: 'Channel App Token', type: 'STRING', required: true, _channel: 'LEAN_TECH' },
      { name: 'paymentDestinationId', label: '支付目的地ID', type: 'STRING', required: true, _channel: 'LEAN_TECH' },
      { name: 'fullNameRetrieved', label: '银行获取的用户全名', type: 'STRING', required: false, _channel: 'LEAN_TECH' },
      { name: 'nameMatchType', label: '同名校验结果', type: 'STRING', required: false, _channel: 'LEAN_TECH', tip: 'PERFECT_MATCH / PARTIAL_MATCH / NO_MATCH / RECONNECT_REQUIRED / NO_RESULT' },
      { name: 'vibans', label: '多个IBAN', type: 'ARRAY', required: true, _channel: 'BANKING_CIRCLE' },
    ] },
  ],
  CARD_BIND: [
    { name: 'recipientId', label: '绑卡ID', type: 'STRING', required: true },
    { name: 'status', label: '绑卡状态', type: 'STRING', required: true, tip: 'PROCESSING / SUCCESS / FAILED' },
  ],
  CARD_QUERY: [
    { name: 'recipientId', label: '绑定银行账户ID', type: 'STRING', required: true },
    { name: 'status', label: '状态', type: 'STRING', required: true, tip: 'PROCESSING / SUCCESS / FAILED' },
    { name: 'channelCode', label: '渠道编码', type: 'STRING', required: true },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: false },
    { name: 'accountName', label: '账户名称', type: 'STRING', required: true },
    { name: 'accountNumber', label: '账户号码', type: 'STRING', required: true },
    { name: 'bic', label: 'BIC/SWIFT', type: 'STRING', required: false },
    { name: 'bankName', label: '银行名称', type: 'STRING', required: false },
    { name: 'currency', label: '币种', type: 'STRING', required: true },
    { name: 'city', label: '城市', type: 'STRING', required: false },
    { name: 'country', label: '国家', type: 'STRING', required: false },
    { name: 'address', label: '地址', type: 'STRING', required: false },
    { name: 'bankCity', label: '银行城市', type: 'STRING', required: false },
    { name: 'bankCountry', label: '银行国家', type: 'STRING', required: false },
    { name: 'bankAddress', label: '银行地址', type: 'STRING', required: false },
    { name: 'errorCode', label: '错误码', type: 'STRING', required: false },
    { name: 'errorMsg', label: '错误信息', type: 'STRING', required: false },
  ],
  CARD_UNBIND: [
    { name: 'recipientId', label: '绑卡ID', type: 'STRING', required: true },
    { name: 'status', label: '状态', type: 'STRING', required: true, tip: 'SUCCESS: 解绑成功' },
  ],
  DEPOSIT_CREATE: [
    { name: 'orderId', label: '订单号', type: 'STRING', required: true },
    { name: 'status', label: '状态', type: 'STRING', required: false, tip: 'PROCESSING，最终状态通过webhook回调' },
    { name: 'externalOrderId', label: '上游订单号', type: 'STRING', required: true },
  ],
  DEPOSIT_QUERY: [
    { name: 'externalOrderId', label: '业务线订单号', type: 'STRING', required: false },
    { name: 'orderId', label: '订单号', type: 'STRING', required: true },
    { name: 'status', label: '充值单状态', type: 'STRING', required: true, tip: 'PROCESSING / SUCCESS / FAILED' },
    { name: 'subStatus', label: '充值子状态', type: 'STRING', required: true, tip: 'CREATED / CREATE_CALLBACK_WAIT / SUCCESS / FAILED' },
    { name: 'refundStatus', label: '退款状态', type: 'STRING', required: true, tip: 'NOT_REFUNDED / REFUNDING / REFUNDED / REFUND_FAILED' },
    { name: 'channelCode', label: '订单使用渠道', type: 'STRING', required: true },
    { name: 'accountId', label: '渠道开户账户ID', type: 'STRING', required: false },
    { name: 'depositMethod', label: '支付方式', type: 'STRING', required: true, tip: 'VIRTUAL_ACCOUNT / PIX / VIETQR / ZEN_WALLET / INTERACE_REQUEST' },
    { name: 'currency', label: '支付币种', type: 'STRING', required: true },
    { name: 'amount', label: '订单金额', type: 'DECIMAL', required: true },
    { name: 'fee', label: '手续费', type: 'DECIMAL', required: true },
    { name: 'errorCode', label: '错误码', type: 'STRING', required: false },
    { name: 'errorMsg', label: '错误信息', type: 'STRING', required: false },
    { name: 'completedTime', label: '完成时间', type: 'LONG', required: false },
    { name: 'createTime', label: '创建时间', type: 'LONG', required: true },
    { name: 'updateTime', label: '更新时间', type: 'LONG', required: false },
    { name: 'remark', label: '备注', type: 'STRING', required: false },
    { name: 'paymentInfo', label: '支付信息', type: 'OBJECT', required: false, tip: '非VA模式充值时返回', children: [
      { name: 'payType', label: '类型', type: 'STRING', required: true, tip: 'URL / QRCODE / AUTHORIZATION_ID' },
      { name: 'payUrl', label: '支付链接', type: 'STRING', required: false },
      { name: 'barCode', label: '二维码', type: 'STRING', required: false },
      { name: 'authorizationId', label: '授权ID', type: 'STRING', required: false, tip: 'payType=AUTHORIZATION_ID 时有值' },
      { name: 'authorizationToken', label: '授权Token', type: 'STRING', required: false, tip: 'payType=AUTHORIZATION_ID 时有值' },
      { name: 'authorizationAppToken', label: '前端授权Token', type: 'STRING', required: false, tip: 'payType=AUTHORIZATION_ID 时有值' },
      { name: 'connectType', label: '前端链接方式', type: 'STRING', required: false, tip: 'payType=AUTHORIZATION_ID 时有值; REVERSE_ENGINEERING / OPEN_BANKING' },
    ] },
    { name: 'senderInfo', label: '付款人信息', type: 'OBJECT', required: false, tip: 'VA模式充值时返回（BANK_FRICK）', children: [
      { name: 'senderAcctNo', label: '发送方账户', type: 'STRING', required: false },
      { name: 'senderAcctName', label: '发送方账户名称', type: 'STRING', required: false },
      { name: 'senderBic', label: 'SWIFT/BIC Code', type: 'STRING', required: false },
      { name: 'senderAddress', label: '发送方地址', type: 'STRING', required: false },
      { name: 'senderCountryCode', label: '发送方国家代码', type: 'STRING', required: false },
      { name: 'purpose', label: '充值意图', type: 'STRING', required: false },
      { name: 'remarks', label: '备注', type: 'STRING', required: false },
      { name: 'tranTime', label: '转账时间', type: 'LONG', required: false },
      { name: 'iban', label: 'IBAN', type: 'STRING', required: false },
      { name: 'vban', label: 'VBAN', type: 'STRING', required: false },
      { name: 'bankName', label: '银行名称', type: 'STRING', required: false },
    ] },
    { name: 'receiverInfo', label: '收款人信息', type: 'OBJECT', required: false, tip: 'VA模式充值时返回（BANK_FRICK）', children: [
      { name: 'holderName', label: '持有人名称', type: 'STRING', required: false },
      { name: 'bankName', label: '银行名称', type: 'STRING', required: false },
      { name: 'iban', label: 'IBAN', type: 'STRING', required: false },
      { name: 'vban', label: 'VBAN', type: 'STRING', required: false },
      { name: 'bic', label: 'BIC', type: 'STRING', required: false },
      { name: 'accountNumber', label: '账号', type: 'STRING', required: false },
    ] },
    { name: 'extParams', label: '渠道扩展信息', type: 'JSON_STRING', required: false, tip: '渠道返回的额外信息，JSON格式', children: [
      { name: 'channelOrderId', label: '渠道订单ID', type: 'STRING', required: true, _channel: 'BANK_FRICK' },
    ] },
  ],
  DEPOSIT_CLOSE: [
    { name: 'orderId', label: '订单号', type: 'STRING', required: true },
    { name: 'externalOrderId', label: '外部订单号', type: 'STRING', required: true },
    { name: 'state', label: '订单状态', type: 'STRING', required: true, tip: 'FAILED(关单成功) / 原状态(关单失败)' },
    { name: 'closeResult', label: '关单结果', type: 'STRING', required: true, tip: 'SUCCESS / PROCESSING / FAILED' },
    { name: 'closeMessage', label: '关单描述', type: 'STRING', required: false },
  ],
  REFUND_CREATE: [
    { name: 'orderId', label: '退款单号', type: 'STRING', required: true },
  ],
  REFUND_QUERY: [
    { name: 'externalOrderId', label: '外部退款单号', type: 'STRING', required: true },
    { name: 'orderId', label: '退款单号', type: 'STRING', required: true },
    { name: 'status', label: '退款状态', type: 'STRING', required: true, tip: 'INIT / PENDING / SUCCESS / FAILED' },
    { name: 'errorCode', label: '错误码', type: 'STRING', required: false },
    { name: 'errorMsg', label: '错误信息', type: 'STRING', required: false },
    { name: 'refundReason', label: '退款原因', type: 'STRING', required: false },
    { name: 'remark', label: '备注', type: 'STRING', required: false },
    { name: 'amount', label: '原订单金额', type: 'DECIMAL', required: true },
    { name: 'arrivalAmount', label: '用户退款到账金额', type: 'DECIMAL', required: false },
    { name: 'currency', label: '币种', type: 'STRING', required: true },
    { name: 'channelCode', label: '渠道', type: 'STRING', required: true },
    { name: 'createTime', label: '创建时间', type: 'LONG', required: true },
    { name: 'updateTime', label: '更新时间', type: 'LONG', required: false },
    { name: 'completedTime', label: '完成时间', type: 'LONG', required: false },
  ],
  WITHDRAW_CREATE: [
    { name: 'orderId', label: '订单号', type: 'STRING', required: true },
    { name: 'status', label: '状态', type: 'STRING', required: true, tip: 'PROCESSING / SUCCESS / FAILED' },
  ],
  WITHDRAW_QUERY: [
    { name: 'externalOrderId', label: '外部订单号', type: 'STRING', required: true },
    { name: 'orderId', label: '订单号', type: 'STRING', required: true },
    { name: 'status', label: '提现单状态', type: 'STRING', required: true, tip: 'PROCESSING / SUCCESS / FAILED' },
    { name: 'subStatus', label: '提现子状态', type: 'STRING', required: true, tip: 'INIT / PRE_CHECKING / CREATING_CHANNEL_ORDER / WAITING_CHANNEL_CALLBACK / SUCCESS / FAILED' },
    { name: 'channelCode', label: '订单使用渠道', type: 'STRING', required: true },
    { name: 'withdrawMethod', label: '提现方式', type: 'STRING', required: true, tip: 'BANK_TRANSFER / PIX_CODE / INTERAC / EFT' },
    { name: 'currency', label: '支付币种', type: 'STRING', required: true },
    { name: 'amount', label: '订单金额', type: 'DECIMAL', required: true },
    { name: 'fee', label: '手续费', type: 'DECIMAL', required: true },
    { name: 'feeCurrency', label: '手续费币种', type: 'STRING', required: true },
    { name: 'createTime', label: '创建时间', type: 'LONG', required: true },
    { name: 'updateTime', label: '更新时间', type: 'LONG', required: true },
    { name: 'completedTime', label: '完成时间', type: 'LONG', required: false },
    { name: 'remark', label: '备注', type: 'STRING', required: false },
    { name: 'errorCode', label: '错误码', type: 'STRING', required: false },
    { name: 'errorMsg', label: '错误信息', type: 'STRING', required: false },
    { name: 'senderInfo', label: '付款人信息', type: 'OBJECT', required: false, children: [
      { name: 'holderName', label: '持有人名称', type: 'STRING', required: false },
      { name: 'bankName', label: '银行名称', type: 'STRING', required: false },
      { name: 'iban', label: 'IBAN', type: 'STRING', required: false },
      { name: 'vban', label: 'VBAN', type: 'STRING', required: false },
      { name: 'bic', label: 'BIC', type: 'STRING', required: false },
      { name: 'accountNumber', label: '账号', type: 'STRING', required: false },
    ] },
    { name: 'receiverInfo', label: '收款人信息', type: 'OBJECT', required: false, children: [
      { name: 'holderName', label: '持有人名称', type: 'STRING', required: false },
      { name: 'bankName', label: '银行名称', type: 'STRING', required: false },
      { name: 'iban', label: 'IBAN', type: 'STRING', required: false },
      { name: 'vban', label: 'VBAN', type: 'STRING', required: false },
      { name: 'bic', label: 'BIC', type: 'STRING', required: false },
      { name: 'accountNumber', label: '账号', type: 'STRING', required: false },
    ] },
    { name: 'channelOrderId', label: '渠道订单号', type: 'STRING', required: false },
    { name: 'extParams', label: '渠道扩展信息', type: 'JSON_STRING', required: false, tip: '渠道返回的额外信息，JSON格式', children: [
      { name: 'channelOrderId', label: '渠道订单ID', type: 'STRING', required: true, _channel: 'BANK_FRICK' },
      { name: 'originalAmount', label: '原始金额', type: 'STRING', required: true, _channel: 'PYVIO' },
      { name: 'originalCurrency', label: '原始币种', type: 'STRING', required: true, _channel: 'PYVIO' },
      { name: 'targetAmount', label: '目标金额', type: 'STRING', required: true, _channel: 'PYVIO' },
      { name: 'targetCurrency', label: '目标币种', type: 'STRING', required: true, _channel: 'PYVIO' },
      { name: 'feeAmount', label: '手续费金额', type: 'STRING', required: true, _channel: 'PYVIO' },
      { name: 'feeCurrency', label: '手续费币种', type: 'STRING', required: true, _channel: 'PYVIO' },
      { name: 'originalAmount', label: '原始 payout 金额', type: 'STRING', required: false, _channel: 'TAZAPAY', tip: '非换汇场景与 amount 一致，换汇场景为 payout 币种金额' },
      { name: 'originalCurrency', label: '原始 payout 币种', type: 'STRING', required: false, _channel: 'TAZAPAY', tip: '非换汇场景与 currency 一致，换汇场景为 payout 币种' },
    ] },
  ],
  CHANNEL_CONFIG_QUERY: [
    { name: 'channelCode', label: '渠道编码', type: 'STRING', required: true },
    { name: 'channelName', label: '渠道名称', type: 'STRING', required: true },
    { name: 'channelType', label: '渠道类型', type: 'STRING', required: true, tip: 'BANK / WALLET / PSP' },
    { name: 'businessType', label: '业务类型', type: 'STRING', required: true, tip: 'BUSINESS / INDIVIDUAL / ALL' },
    { name: 'region', label: '区域', type: 'STRING', required: false },
    { name: 'country', label: '国家', type: 'STRING', required: true },
    { name: 'currency', label: '渠道结算币种', type: 'STRING', required: true },
    { name: 'sameNameVa', label: '是否需要VA同名', type: 'STRING', required: true, tip: 'true / false' },
    { name: 'paymentMethod', label: '支付方式', type: 'STRING', required: true },
    { name: 'needOpenAccount', label: '是否需要开户', type: 'BOOLEAN', required: true },
    { name: 'depositOrderMode', label: '充值订单模式', type: 'STRING', required: false, tip: 'OFFLINE_DEPOSIT / PRE_ORDER' },
    { name: 'supportDeposit', label: '支持充值', type: 'BOOLEAN', required: true },
    { name: 'supportWithdraw', label: '支持提现', type: 'BOOLEAN', required: true },
    { name: 'supportRefund', label: '支持退款', type: 'BOOLEAN', required: true },
    { name: 'supportPartialRefund', label: '支持部分退款', type: 'BOOLEAN', required: true },
  ],
  ORDER_PAGE: [
    { name: 'total', label: '总数', type: 'INTEGER', required: true },
    { name: 'page', label: '页码', type: 'INTEGER', required: true },
    { name: 'pageSize', label: '每页条数', type: 'INTEGER', required: true },
    { name: 'list[].channelCode', label: '渠道编号', type: 'STRING', required: true },
    { name: 'list[].currency', label: '币种', type: 'STRING', required: true },
    { name: 'list[].businessPartner', label: '合作伙伴', type: 'STRING', required: true },
    { name: 'list[].orderId', label: '订单号', type: 'STRING', required: true },
    { name: 'list[].accountId', label: '账户号', type: 'STRING', required: true },
    { name: 'list[].businessType', label: '业务类型', type: 'STRING', required: true, tip: 'DEPOSIT / WITHDRAW / REFUND' },
    { name: 'list[].transAmount', label: '交易金额', type: 'DECIMAL', required: true },
    { name: 'list[].transTime', label: '交易时间', type: 'LONG', required: true },
    { name: 'list[].status', label: '订单状态', type: 'STRING', required: true },
    { name: 'list[].isRefund', label: '是否退款', type: 'BOOLEAN', required: true },
    { name: 'list[].refundOrderId', label: '退款单号', type: 'STRING', required: false },
    { name: 'list[].externalUserId', label: '外部用户ID', type: 'STRING', required: false },
  ],
  BANK_LIST: [
    { name: 'identifier', label: '银行唯一标识', type: 'STRING', required: true },
    { name: 'name', label: '银行名称', type: 'STRING', required: true },
    { name: 'logo', label: 'Logo URL', type: 'STRING', required: true },
    { name: 'countryCode', label: '国家码', type: 'STRING', required: true, tip: 'ISO 三字母国家代码' },
    { name: 'transferLimits', label: '转账限额', type: 'ARRAY', required: false, children: [
      { name: 'currency', label: '币种', type: 'STRING', required: true, tip: 'ISO 4217' },
      { name: 'min', label: '最小转账金额', type: 'STRING', required: true },
      { name: 'max', label: '最大转账金额', type: 'STRING', required: true },
    ] },
    { name: 'internationalTransferLimits', label: '国际转账限额', type: 'ARRAY', required: false, children: [
      { name: 'currency', label: '币种', type: 'STRING', required: true, tip: 'ISO 4217' },
      { name: 'min', label: '最小转账金额', type: 'STRING', required: true },
      { name: 'max', label: '最大转账金额', type: 'STRING', required: true },
    ] },
  ],
  FILE_UPLOAD: [
    { name: 'fileId', label: '文件ID', type: 'STRING', required: true },
    { name: 'status', label: '状态', type: 'STRING', required: true, tip: 'PROCESSING / SUCCESS / FAILED / REPLACED' },
    { name: 'bizId', label: '业务ID', type: 'STRING', required: true },
  ],
  FILE_STATUS_QUERY: [
    { name: 'fileId', label: '文件ID', type: 'STRING', required: true },
    { name: 'status', label: '状态', type: 'STRING', required: true, tip: 'PROCESSING / SUCCESS / FAILED / REPLACED' },
    { name: 'bizId', label: '业务ID', type: 'STRING', required: true },
  ],
  USER_CREATE: [
    { name: 'userId', label: '用户ID', type: 'STRING', required: true },
    { name: 'clientId', label: '渠道ClientID', type: 'STRING', required: true, tip: '多个externalUserId可对应同一个clientId' },
    { name: 'status', label: '状态', type: 'STRING', required: true, tip: 'PROCESSING / SUCCESS / FAILED / DENIED' },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true },
    { name: 'failedReason', label: '失败原因', type: 'STRING', required: false },
  ],
  USER_QUERY: [
    { name: 'userId', label: '用户ID', type: 'STRING', required: true },
    { name: 'channelUserId', label: '渠道用户ID', type: 'STRING', required: true },
    { name: 'clientId', label: '渠道ClientID', type: 'STRING', required: true },
    { name: 'status', label: '状态', type: 'STRING', required: true, tip: 'PROCESSING / SUCCESS / FAILED / DENIED' },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true },
    { name: 'failedReason', label: '失败原因', type: 'STRING', required: false },
  ],
  BALANCE_QUERY: [
    { name: 'currency', label: '币种', type: 'STRING', required: true },
    { name: 'channelCode', label: '渠道', type: 'STRING', required: true },
    { name: 'availableBalance', label: '可用余额', type: 'STRING', required: true },
  ],
  RATE_QUERY: [
    { name: 'exchangeRate', label: '汇率', type: 'STRING', required: true, tip: '格式: 0.0000000000' },
    { name: 'rateId', label: '汇率ID', type: 'STRING', required: true, tip: '后续提现使用' },
    { name: 'originCurrency', label: '原始币种', type: 'STRING', required: true },
    { name: 'targetCurrency', label: '目标币种', type: 'STRING', required: true },
    { name: 'expireTime', label: '过期时间', type: 'STRING', required: true, tip: '时间戳' },
  ],
  PAYOUT_REQUIREMENTS: [
    { name: 'country', label: '国家', type: 'STRING', required: true },
    { name: 'currency', label: '币种', type: 'STRING', required: true },
    { name: 'channelCode', label: '渠道', type: 'STRING', required: true },
    { name: 'payoutMethods', label: 'Payout 方式列表', type: 'ARRAY', required: true, tip: '每个元素包含 payoutType、requiredFields、recommendedFields、transferLimit', children: [
      { name: 'payoutType', label: 'Payout 类型', type: 'STRING', required: true, tip: 'swift / local' },
      { name: 'beneficiaryTypes', label: '支持的收款人类型', type: 'ARRAY', required: true, tip: 'individual / business' },
      { name: 'requiredFields', label: '必填字段', type: 'OBJECT', required: true, children: [
        { name: 'mainFields', label: '主字段（FiatCardBindRequest 直接字段）', type: 'ARRAY', required: false, tip: '可能的值: accountName / bic / accountNumber / bankName / iban。具体哪些必填取决于走廊，通过本接口动态返回' },
        { name: 'extParamFields', label: 'extParam 中的字段', type: 'ARRAY', required: false, tip: '可能的值: ifscCode / abaCode / sortCode / bankCode / addressLine1 / addressLine2 / city / state / postalCode / addressCountry / taxId / nationality / dateOfBirth / nationalIdentificationNumber / registrationNumber / email / accountType / branchName。具体哪些必填取决于走廊' },
      ] },
      { name: 'recommendedFields', label: '推荐字段（非必填但建议传）', type: 'OBJECT', required: false, children: [
        { name: 'mainFields', label: '主字段', type: 'ARRAY', required: false, tip: '可能的值: bic / accountName / bankName / accountNumber / iban' },
        { name: 'extParamFields', label: 'extParam 中的字段', type: 'ARRAY', required: false, tip: '可能的值: addressLine1 / city / state / postalCode / nationality / email 等' },
      ] },
      { name: 'transferLimit', label: '转账限额', type: 'OBJECT', required: false, children: [
        { name: 'currency', label: '币种', type: 'STRING', required: true },
        { name: 'minimum', label: '最小金额', type: 'STRING', required: true },
        { name: 'maximum', label: '最大金额', type: 'STRING', required: true },
      ] },
      { name: 'fundTransferNetworks', label: '本地清算网络', type: 'ARRAY', required: false, tip: '仅 local 模式，如 FedACH / Fedwire / CHATS / FPS' },
    ] },
  ],
};

// 通用接口的示例请求 & 响应
const API_EXAMPLES = {
  ACCOUNT_CLOSE: {
    request: { requestId: 'your_request_id_here', externalUserId: 'your_external_user_id_here', accountId: 'your_account_id_here' },
    response: { code: '200', subCode: '000000', data: true, msg: 'success', requestTime: 1702609200000, requestId: 'close_req_9876543210' },
  },
  ACCOUNT_QUERY: {
    request: { accountId: 'AC20260108SG00000155', requestId: '123123123123123' },
    response: { code: '200', subCode: '000000', msg: 'success', requestTime: 1702598400000, requestId: 'req_1702598400123_abc123', data: { accountId: 'ACC202312150001', status: 'ACTIVE', subStatus: 'SUCCESS', externalUserId: 'user123456', channelCode: 'EQUALS_MONEY', paymentMethod: 'VIRTUAL_ACCOUNT', currency: 'USD', redirectUrl: null, errorCode: null, errorMsg: null, createTime: 1702502000000, updateTime: 1702598400000, accountDetail: { accountName: 'JOHN DOE', accountNumber: '123456789012', bic: 'BOFAUS3NXXX', bankName: 'BANK OF AMERICA', bankCountry: 'United States', bankAddress: '100 NORTH TRYON STREET' }, userType: 'INDIVIDUAL', extParam: null } },
  },
  CARD_QUERY: {
    request: { requestId: 'your_request_id_here', recipientId: 'your_receipt_id_here' },
    response: { code: '200', subCode: '000000', msg: 'success', requestTime: 1702598400000, requestId: 'req_1702598400123_abc123', data: { recipientId: 'RECP202312150001', status: 'SUCCESS', channelCode: 'EQUALS_MONEY', externalUserId: 'user123456', accountName: 'JOHN DOE', accountNumber: '123456789012', bic: 'BOFAUS3NXXX', bankName: 'BANK OF AMERICA', currency: 'USD', city: 'CHARLOTTE', country: 'USA', address: '123 MAIN STREET', bankCity: 'CHARLOTTE', bankCountry: 'USA', bankAddress: '100 NORTH TRYON STREET', errorCode: null, errorMsg: null } },
  },
  CARD_UNBIND: {
    request: { recipientId: 'RP20260304BC00000134', channelCode: 'BANK_FRICK', requestId: 'REQ_20260129_00003' },
    response: { code: '200', subCode: '000000', msg: 'success', requestTime: 1702609200000, requestId: 'REQ_20260129_00003', data: { recipientId: 'RP20260304BC00000134', status: 'SUCCESS' } },
  },
  DEPOSIT_QUERY: {
    request: { requestId: 'req_1234567890', orderId: 'DP2025121511060001' },
    response: { code: '200', subCode: '000000', msg: 'success', requestTime: 1702609200000, requestId: 'req_1234567890', data: { externalOrderId: 'EXT_ORD_123', orderId: 'DP2025121511060001', status: 'SUCCESS', subStatus: 'SUCCESS', refundStatus: 'NOT_REFUNDED', channelCode: 'EQUALS_MONEY', accountId: '1000001', depositMethod: 'VIRTUAL_ACCOUNT', currency: 'EUR', amount: 300.00, fee: 5.00, errorCode: null, errorMsg: null, completedTime: 1702609200000, createTime: 1702609200000, updateTime: 1702609200000 } },
  },
  DEPOSIT_CLOSE: {
    request: { requestId: 'req_close_001', orderId: 'DEP202312150001' },
    response: { code: '200', subCode: '000000', msg: 'success', requestTime: 1778136838716, requestId: 'req_close_001', data: { orderId: 'DEP202312150001', externalOrderId: 'ext_dep_001', state: 'FAILED', closeResult: 'SUCCESS', closeMessage: 'Order closed successfully' } },
  },
  REFUND_CREATE: {
    request: { requestId: 'req_refund_01', externalOrderId: 'EXT_REF_01', depositOrderId: 'D12345678', callbackUrl: 'https://merchant.com/callback', description: 'Customer canceled' },
    response: { code: '200', subCode: '000000', msg: 'success', requestTime: 1702609200000, requestId: 'req_1234567890', data: { orderId: '1000001' } },
  },
  REFUND_QUERY: {
    request: { requestId: 'req_q_ref_1', externalOrderId: 'EXT_REF_01' },
    response: { code: '200', subCode: '000000', msg: 'success', requestTime: 1702609200000, requestId: 'req_1234567890', data: { externalOrderId: 'BL202310270001', orderId: 'RF123456789', status: 'SUCCESS', errorCode: null, errorMsg: null, refundReason: '用户取消订单', remark: '通过APP申请', amount: 150.88, arrivalAmount: 150.00, currency: 'EUR', channelCode: 'EQUALS_MONEY', createTime: 1702609200000, updateTime: 1702609200000, completedTime: 1702609200000 } },
  },
  WITHDRAW_QUERY: {
    request: { requestId: 'A123123123', externalOrderId: 'EXT_WITH_71', orderId: 'WD20251218EM00000020' },
    response: { code: '200', subCode: '000000', msg: 'success', requestTime: 1702598400000, requestId: 'req_withdraw_01', data: { externalOrderId: 'EXT_WITH_01', orderId: 'WD20251218EM00000020', status: 'SUCCESS', subStatus: 'SUCCESS', channelCode: 'EQUALS_MONEY', withdrawMethod: 'BANK_TRANSFER', currency: 'EUR', amount: 200.00, fee: 1.50, feeCurrency: 'EUR', createTime: 1702598400000, updateTime: 1702598400000, completedTime: 1702598400000, channelOrderId: 'P2603102019265845234459680', errorCode: null, errorMsg: null } },
  },
  CHANNEL_CONFIG_QUERY: {
    request: { requestId: 'req_q_bal', channelCode: 'EQUALS_MONEY', currency: 'USD' },
    response: { code: 200, subCode: '000000', msg: 'Success', requestTime: '1766644949846', requestId: '12313312312315', data: [{ channelCode: 'EQUALS_MONEY', channelName: 'Equals Money', channelType: 'BANK', businessType: 'ALL', region: 'EU', country: 'UK', currency: 'USD', sameNameVa: true, paymentMethod: 'SWIFT', needOpenAccount: true, depositOrderMode: 'OFFLINE_DEPOSIT', supportDeposit: true, supportWithdraw: true, supportRefund: false, supportPartialRefund: false }] },
  },
  ORDER_PAGE: {
    request: { requestId: 'recon_20260106_sgb', channelCode: 'SGB', filters: { orderType: 'DEPOSIT', currency: 'USD', orderStatusList: ['SUCCESS', 'FAILED'] }, timeRange: { startTime: 1767628800000, endTime: 1767715199000 }, pagination: { page: 1, pageSize: 100 } },
    response: { code: '200', subCode: '000000', msg: 'success', requestTime: 1702609200000, requestId: 'req_1234567890', data: { total: 1, page: 1, pageSize: 100, list: [{ channelCode: 'SGB', currency: 'USD', orderId: 'DP20260106PS00000047', accountId: 'AC20251229SG00000075', businessType: 'DEPOSIT', transAmount: 100.00, businessPartner: 'BIZPAY', transTime: 1748396290481, status: 'SUCCESS', isRefund: false, refundOrderId: null, externalUserId: 'user123456' }] } },
  },
  BANK_LIST: {
    request: { channelCode: 'LEAN_TECH', requestId: '213213234123' },
    response: { code: 200, subCode: '000000', msg: 'Success', requestId: '213213234123', data: [{ countryCode: 'SAU', identifier: 'LEANMB1_SAU', name: 'Lean Mockbank', logo: 'https://cdn.leantech.me/img/banks/white-gmockbank1.png', transferLimits: [{ currency: 'USD', max: '27000.000', min: '10.000' }] }] },
  },
  FILE_UPLOAD: {
    request: { channelCode: 'EQUALS_MONEY', scene: 'KYC_DOCUMENT', documentType: 'PASSPORT', bizId: 'account_123456', externalUserId: 'user_9988', requestId: 'req_20240101001', documentCountry: 'SG', documentSide: 'FRONT', file: '(binary)' },
    response: { code: '200', subCode: '000000', message: 'success', requestId: 'req-123', data: { fileId: 'CF202401010001', bizId: 'ACCOUNT_10001', status: 'PROCESSING' } },
  },
  FILE_STATUS_QUERY: {
    request: { requestId: 'req_20260116_000123', fileIds: ['FILE_202601160001', 'FILE_202601160002', 'FILE_202601160003'] },
    response: { code: '200', subCode: '000000', message: 'success', requestId: 'req-456', data: [{ fileId: 'CF202401010001', bizId: 'ACCOUNT_10001', status: 'PROCESSING' }, { fileId: 'CF202401010002', bizId: 'ACCOUNT_10001', status: 'SUCCESS' }] },
  },
  USER_CREATE: {
    request: { requestId: '1020260121769958461', operateType: 'CREATE', channelCode: 'PYVIO', externalUserId: 'USER_ID_202602021033030', userType: 'BUSINESS', extParam: { email: 'email@gmail.com', merchantType: 'B2B', location: 'HK', industry: ['TRANSLATION_LANGUAGES', 'TELECOMMUNICATIONS'] } },
    response: { code: 200, subCode: '000000', msg: 'Success', requestId: '1020260121769958461', data: { externalUserId: 'USER_ID_202602021033030', status: 'PROCESSING', userId: 'UR20260202PV00000066', clientId: 'CT2016336879406353129', failedReason: null } },
  },
  USER_QUERY: {
    request: { requestId: 'RequestId_000001', userId: 'UR20260130PV00000002', externalUserId: 'USERID_100015' },
    response: { code: 200, subCode: '000000', msg: 'Success', requestId: 'RequestId_000001', data: { clientId: 'CT2016336879406353129', channelUserId: 'UT2016336879406353130', externalUserId: 'USERID_100015', status: 'DENIED', userId: 'UR20260130PV00000002' } },
  },
  BALANCE_QUERY: {
    request: { channelCode: 'PYVIO', currency: 'USD', requestId: 'REQ_20260129_00002' },
    response: { code: 200, subCode: '000000', msg: 'Success', requestId: 'REQ_20260129_00002', data: [{ availableBalance: '117659.62', channelCode: 'PYVIO', currency: 'USD' }] },
  },
  RATE_QUERY: {
    request: { originCurrency: 'USD', targetCurrency: 'NGN', channelCode: 'TAZAPAY', destinationCountry: 'NG', amount: 10000, payoutType: 'LOCAL_PAYMENT_NETWORK', requestId: 'REQ_20260418_00001' },
    response: { code: 200, subCode: '000000', msg: 'Success', requestId: 'REQ_20260418_00001', data: { exchangeRate: '1520.5000000000', expireTime: '1770031852995', originCurrency: 'USD', rateId: 'poq_abc123def456', targetCurrency: 'NGN' } },
  },
  PAYOUT_REQUIREMENTS: {
    request: { channelCode: 'TAZAPAY', country: 'US', currency: 'USD', requestId: 'req_payout_req_001' },
    response: { code: 200, subCode: '000000', msg: 'Success', requestId: 'req_payout_req_001', data: { country: 'US', currency: 'USD', channelCode: 'TAZAPAY', payoutMethods: [{ payoutType: 'swift', beneficiaryTypes: ['individual', 'business'], requiredFields: { mainFields: ['accountName', 'bankName', 'accountNumber', 'bic'], extParamFields: null }, recommendedFields: { mainFields: null, extParamFields: ['addressLine1', 'city', 'state', 'postalCode'] }, transferLimit: { currency: 'USD', minimum: '1.00', maximum: '100000000.00' }, fundTransferNetworks: null }, { payoutType: 'local', beneficiaryTypes: ['individual', 'business'], requiredFields: { mainFields: ['accountName', 'bankName', 'accountNumber'], extParamFields: ['abaCode'] }, recommendedFields: { mainFields: ['bic'], extParamFields: ['addressLine1', 'city', 'state', 'postalCode'] }, transferLimit: { currency: 'USD', minimum: '0.01', maximum: '10000000.00' }, fundTransferNetworks: [{ name: 'fedach', description: 'FedACH batch clearing', transferLimit: { currency: 'USD', minimum: '0.01', maximum: '1000000.00' } }, { name: 'fedwire', description: 'Fedwire real-time RTGS', transferLimit: { currency: 'USD', minimum: '0.01', maximum: '10000000.00' } }] }] } },
  },
};

const CHANNEL_SPECIFIC_APIS = new Set(['ACCOUNT_CREATE', 'CARD_BIND', 'DEPOSIT_CREATE', 'DEPOSIT_CLOSE', 'WITHDRAW_CREATE', 'REFUND_CREATE', 'USER_CREATE']);

const getSceneOptions = (t) => [
  { label: t('doc_grpAccount'), options: [
    { value: 'ACCOUNT_CREATE', label: '👤 ' + t('doc_apiAccountCreate') },
    { value: 'ACCOUNT_CLOSE', label: '🚪 ' + t('doc_apiAccountClose') },
    { value: 'ACCOUNT_QUERY', label: '🔍 ' + t('doc_apiAccountQuery') },
  ]},
  { label: t('doc_grpBankAccount'), options: [
    { value: 'PAYOUT_REQUIREMENTS', label: '📋 ' + t('doc_apiPayoutRequirements') },
    { value: 'CARD_BIND', label: '💳 ' + t('doc_apiCardBind') },
    { value: 'CARD_QUERY', label: '🔎 ' + t('doc_apiCardQuery') },
    { value: 'CARD_UNBIND', label: '✂️ ' + t('doc_apiCardUnbind') },
  ]},
  { label: t('doc_grpDeposit'), options: [
    { value: 'DEPOSIT_CREATE', label: '💰 ' + t('doc_apiDepositCreate') },
    { value: 'DEPOSIT_QUERY', label: '📋 ' + t('doc_apiDepositQuery') },
    { value: 'DEPOSIT_CLOSE', label: '🚫 ' + t('doc_apiDepositClose') },
  ]},
  { label: t('doc_grpWithdraw'), options: [
    { value: 'WITHDRAW_CREATE', label: '🏦 ' + t('doc_apiWithdrawCreate') },
    { value: 'WITHDRAW_QUERY', label: '📄 ' + t('doc_apiWithdrawQuery') },
  ]},
  { label: t('doc_grpRefund'), options: [
    { value: 'REFUND_CREATE', label: '↩️ ' + t('doc_apiRefundCreate') },
    { value: 'REFUND_QUERY', label: '📑 ' + t('doc_apiRefundQuery') },
  ]},
  { label: t('doc_grpUser'), options: [
    { value: 'USER_CREATE', label: '👥 ' + t('doc_apiUserCreate') },
    { value: 'USER_QUERY', label: '🧑 ' + t('doc_apiUserQuery') },
  ]},
  { label: t('doc_grpFile'), options: [
    { value: 'FILE_UPLOAD', label: '📤 ' + t('doc_apiFileUpload') },
    { value: 'FILE_STATUS_QUERY', label: '📁 ' + t('doc_apiFileStatusQuery') },
  ]},
  { label: t('doc_grpGeneral'), options: [
    { value: 'CHANNEL_CONFIG_QUERY', label: '⚙️ ' + t('doc_apiChannelConfig') },
    { value: 'ORDER_PAGE', label: '📊 ' + t('doc_apiOrderPage') },
    { value: 'BANK_LIST', label: '🏛️ ' + t('doc_apiBankList') },
    { value: 'BALANCE_QUERY', label: '💵 ' + t('doc_apiBalanceQuery') },
    { value: 'RATE_QUERY', label: '💱 ' + t('doc_apiRateQuery') },
  ]},
];

const getCommonResponseFields = (t) => [
  { name: 'code', label: t('doc_colType') === 'Type' ? 'Status Code' : '状态码', type: 'STRING', required: true, tip: '200 = success' },
  { name: 'subCode', label: t('doc_colType') === 'Type' ? 'Business Code' : '业务状态码', type: 'STRING', required: true, tip: '000000 = success' },
  { name: 'msg', label: t('doc_colType') === 'Type' ? 'Message' : '响应消息', type: 'STRING', required: true, tip: 'e.g. success' },
  { name: 'requestId', label: t('doc_colType') === 'Type' ? 'Request ID' : '请求ID', type: 'STRING', required: true, tip: t('doc_colType') === 'Type' ? 'Echoed from request' : '原样返回请求中的 requestId' },
  { name: 'requestTime', label: t('doc_colType') === 'Type' ? 'Request Time' : '请求时间', type: 'LONG', required: true, tip: t('doc_colType') === 'Type' ? 'Epoch milliseconds' : '毫秒时间戳' },
  { name: 'data', label: t('doc_colType') === 'Type' ? 'Business Data' : '业务数据', type: 'OBJECT', required: false, tip: t('doc_colType') === 'Type' ? 'See individual API response params' : '具体结构见各接口响应参数' },
];

const getErrorCodes = (t) => [
  { code: '000000', desc: t('doc_err000000'), action: t('doc_err000000Action'), cat: 'success' },
  { code: '400001', desc: t('doc_err400001'), action: t('doc_err400001Action'), cat: 'client' },
  { code: '400002', desc: t('doc_err400002'), action: t('doc_err400002Action'), cat: 'client' },
  { code: '400003', desc: t('doc_err400003'), action: t('doc_err400003Action'), cat: 'client' },
  { code: '400004', desc: t('doc_err400004'), action: t('doc_err400004Action'), cat: 'client' },
  { code: '400005', desc: t('doc_err400005'), action: t('doc_err400005Action'), cat: 'client' },
  { code: '400006', desc: t('doc_err400006'), action: t('doc_err400006Action'), cat: 'client' },
  { code: '400201', desc: t('doc_err400201'), action: t('doc_err400201Action'), cat: 'signature' },
  { code: '400301', desc: t('doc_err400301'), action: t('doc_err400301Action'), cat: 'verification' },
  { code: '400401', desc: t('doc_err400401'), action: t('doc_err400401Action'), cat: 'risk' },
  { code: '400402', desc: t('doc_err400402'), action: t('doc_err400402Action'), cat: 'risk' },
  { code: '500001', desc: t('doc_err500001'), action: t('doc_err500001Action'), cat: 'system' },
  { code: '500002', desc: t('doc_err500002'), action: t('doc_err500002Action'), cat: 'system' },
  { code: '500003', desc: t('doc_err500003'), action: t('doc_err500003Action'), cat: 'system' },
  { code: '500004', desc: t('doc_err500004'), action: t('doc_err500004Action'), cat: 'system' },
  { code: '500005', desc: t('doc_err500005'), action: t('doc_err500005Action'), cat: 'system' },
  { code: '500006', desc: t('doc_err500006'), action: t('doc_err500006Action'), cat: 'system' },
  { code: '500007', desc: t('doc_err500007'), action: t('doc_err500007Action'), cat: 'system' },
  { code: '500008', desc: t('doc_err500008'), action: t('doc_err500008Action'), cat: 'system' },
  { code: '500009', desc: t('doc_err500009'), action: t('doc_err500009Action'), cat: 'system' },
  { code: '500010', desc: t('doc_err500010'), action: t('doc_err500010Action'), cat: 'system' },
];

const getErrorCatConfig = (t) => ({
  success: { label: t('doc_catSuccess'), color: '#22c55e', bg: '#f0fdf4' },
  client: { label: t('doc_catClient'), color: '#f59e0b', bg: '#fffbeb' },
  signature: { label: t('doc_catSignature'), color: '#8b5cf6', bg: '#f5f3ff' },
  verification: { label: t('doc_catVerification'), color: '#06b6d4', bg: '#ecfeff' },
  risk: { label: t('doc_catRisk'), color: '#ec4899', bg: '#fdf2f8' },
  system: { label: t('doc_catSystem'), color: '#ef4444', bg: '#fef2f2' },
});

// ─── Method Badge (Checkout.com style) ───
const MethodBadge = ({ method }) => {
  const styles = {
    POST: { bg: '#22c55e', color: '#fff' },
    GET: { bg: '#3b82f6', color: '#fff' },
    PUT: { bg: '#f59e0b', color: '#fff' },
    DELETE: { bg: '#ef4444', color: '#fff' },
  };
  const s = styles[method] || styles.GET;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
      fontFamily: "'JetBrains Mono', 'SF Mono', monospace",
      background: s.bg, color: s.color, letterSpacing: '0.5px', lineHeight: '18px',
      minWidth: 42, textAlign: 'center',
    }}>{method}</span>
  );
};

// ─── JSON Editor with format/compress/validate ───
function JsonEditor({ value, onChange, rows = 10, placeholder }) {
  const [error, setError] = useState(null);
  const handleChange = (e) => { onChange?.(e.target.value); setError(null); };
  const handleFormat = () => {
    if (!value) return;
    try { onChange(JSON.stringify(JSON.parse(value), null, 2)); setError(null); }
    catch { setError('JSON 格式错误'); }
  };
  const handleCompress = () => {
    if (!value) return;
    try { onChange(JSON.stringify(JSON.parse(value))); setError(null); }
    catch { setError('JSON 格式错误'); }
  };
  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
        <Button size="small" type="default" onClick={handleFormat} style={{ fontSize: 11, padding: '0 8px', height: 24 }}>✨ 格式化</Button>
        <Button size="small" type="default" onClick={handleCompress} style={{ fontSize: 11, padding: '0 8px', height: 24 }}>📦 压缩</Button>
        {error && <span style={{ fontSize: 11, color: '#ef4444', lineHeight: '24px' }}>⚠️ {error}</span>}
        {!error && value && (() => { try { const arr = JSON.parse(value); return <span style={{ fontSize: 11, color: '#22c55e', lineHeight: '24px' }}>✓ {Array.isArray(arr) ? `${arr.length} items` : 'valid'}</span>; } catch { return null; } })()}
      </div>
      <Input.TextArea
        value={value} onChange={handleChange} rows={rows} placeholder={placeholder}
        style={{ fontFamily: "'JetBrains Mono', 'SF Mono', Consolas, monospace", fontSize: 12, lineHeight: 1.6, background: '#fafbfc', border: error ? '1px solid #ef4444' : undefined }}
      />
    </div>
  );
}

// ─── Dark Code Panel (Checkout.com style right panel) ───
function DarkCodePanel({ title, data, t }) {
  const [copied, setCopied] = useState(false);
  const jsonStr = JSON.stringify(data, null, 2);
  const handleCopy = () => {
    navigator.clipboard.writeText(jsonStr);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div style={{ borderRadius: 8, overflow: 'hidden', border: `1px solid ${COLORS.codeBg}`, marginBottom: 16 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 14px', background: COLORS.codeHeaderBg, borderBottom: `1px solid rgba(255,255,255,0.06)`,
      }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</span>
        <Button size="small" type="text" icon={copied ? <CheckOutlined style={{ color: '#22c55e' }} /> : <CopyOutlined />}
          onClick={handleCopy}
          style={{ fontSize: 11, color: copied ? '#22c55e' : '#64748b', padding: '0 4px', height: 22 }}>
          {copied ? t('doc_copied') : t('doc_copy')}
        </Button>
      </div>
      <div style={{ background: COLORS.codeBg, padding: '12px 14px', maxHeight: 500, overflow: 'auto' }}>
        <ReactJson src={data} name={false} collapsed={2} displayDataTypes={false} enableClipboard={false}
          theme="ocean" style={{ background: 'transparent', fontSize: 12, fontFamily: "'JetBrains Mono', 'SF Mono', monospace" }} />
      </div>
    </div>
  );
}

// ─── Channel Capability Matrix ───
function ChannelCapabilityMatrix({ t }) {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterName, setFilterName] = useState(null);
  const [filterBizType, setFilterBizType] = useState(null);
  const [filterRegion, setFilterRegion] = useState(null);
  const [detailData, setDetailData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await metaApi.getChannelConfigs();
        const body = res.data;
        const list = body?.data?.responseBody || body?.responseBody || body?.data || (Array.isArray(body) ? body : []);
        setConfigs(list);
      } catch (e) { console.error('Failed to load channel configs', e); }
      finally { setLoading(false); }
    })();
  }, []);

  const nameOptions = useMemo(() => [...new Set(configs.map(c => c.channelName || c.channelCode).filter(Boolean))].sort().map(n => {
    const row = configs.find(c => (c.channelName || c.channelCode) === n);
    const l = getChannelLogo(row?.channelCode || n);
    return { label: `${l.emoji} ${n}`, value: n };
  }), [configs]);

  const regionOptions = useMemo(() => [...new Set(configs.map(c => c.region).filter(Boolean))].sort().map(r => ({ label: r, value: r })), [configs]);

  const filtered = useMemo(() => configs.filter(c => {
    if (filterName && (c.channelName || c.channelCode) !== filterName) return false;
    if (filterBizType && c.businessType !== filterBizType) return false;
    if (filterRegion && c.region !== filterRegion) return false;
    return true;
  }), [configs, filterName, filterBizType, filterRegion]);

  // Stats
  const uniqueChannels = useMemo(() => new Set(configs.map(c => c.channelCode)).size, [configs]);
  const uniqueRegions = useMemo(() => new Set(configs.map(c => c.region).filter(Boolean)).size, [configs]);
  const uniqueCurrencies = useMemo(() => new Set(configs.map(c => c.currency).filter(Boolean)).size, [configs]);

  const typeColor = { BANK: 'blue', WALLET: 'purple', PSP: 'orange' };
  const bizColor = { ALL: 'blue', BUSINESS: 'cyan', INDIVIDUAL: 'geekblue' };

  const capDot = (active) => (
    <span style={{
      display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
      background: active ? '#22c55e' : '#e5e7eb',
      boxShadow: active ? '0 0 0 3px rgba(34,197,94,0.15)' : 'none',
      transition: 'all 0.2s',
    }} />
  );

  const capPill = (label, active) => (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: 11, fontWeight: 500, lineHeight: '22px',
      padding: '0 8px', borderRadius: 12, whiteSpace: 'nowrap',
      background: active ? 'rgba(34,197,94,0.08)' : '#f9fafb',
      color: active ? '#15803d' : '#c0c5cc',
      border: `1px solid ${active ? 'rgba(34,197,94,0.2)' : '#eef0f3'}`,
      transition: 'all 0.2s',
    }}>
      {capDot(active)} {label}
    </span>
  );

  const statCard = (icon, value, label, color) => (
    <div style={{
      flex: 1, minWidth: 140, padding: '16px 20px',
      background: '#fff', borderRadius: 10,
      border: '1px solid #eef0f3',
      display: 'flex', alignItems: 'center', gap: 14,
      boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10,
        background: `${color}10`, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        fontSize: 18,
      }}>{icon}</div>
      <div>
        <div style={{ fontSize: 24, fontWeight: 700, color: COLORS.textPrimary, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
        <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      </div>
    </div>
  );

  return (
    <div>
      {/* Stats row */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {statCard('🏦', uniqueChannels, t('doc_statChannels'), '#635bff')}
        {statCard('🌍', uniqueRegions, t('doc_statRegions'), '#3b82f6')}
        {statCard('💱', uniqueCurrencies, t('doc_statCurrencies'), '#f59e0b')}
        {statCard('📋', configs.length, t('doc_statConfigurations'), '#22c55e')}
      </div>

      {/* Filter bar */}
      <div style={{
        marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
        padding: '12px 16px', background: '#fff', borderRadius: 10,
        border: '1px solid #eef0f3', boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
      }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.textSecondary, marginRight: 4 }}>{t('doc_filterLabel')}</span>
        <Select placeholder={t('doc_colChannel')} allowClear showSearch style={{ width: 200 }} onChange={setFilterName} options={nameOptions} size="middle" variant="outlined" />
        <Select placeholder={t('doc_colRegion')} allowClear style={{ width: 130 }} onChange={setFilterRegion} options={regionOptions} size="middle" variant="outlined" />
        <Select placeholder={t('doc_channelType')} allowClear style={{ width: 150 }} onChange={setFilterBizType} options={['ALL', 'BUSINESS', 'INDIVIDUAL'].map(v => ({ label: v, value: v }))} size="middle" variant="outlined" />
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            fontSize: 12, fontWeight: 600, color: COLORS.accent,
            background: COLORS.accentBg, padding: '3px 10px', borderRadius: 12,
          }}>{filtered.length}</span>
          <span style={{ fontSize: 12, color: COLORS.textMuted }}>/ {configs.length}</span>
        </div>
      </div>

      {/* Table */}
      <div style={{
        background: '#fff', borderRadius: 10, border: '1px solid #eef0f3',
        overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
      }}>
        <Table dataSource={filtered} rowKey={(r, i) => `${r.channelCode}_${r.currency}_${i}`} size="middle" loading={loading} scroll={{ x: 1500 }}
          pagination={{ pageSize: 15, showSizeChanger: true, showTotal: n => `${n} items`, size: 'small' }}
          className="channel-matrix-table"
          columns={[
            { title: t('doc_colChannel'), dataIndex: 'channelName', width: 180, fixed: 'left',
              render: (v, r) => {
                const logo = getChannelLogo(r.channelCode);
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: 8,
                      background: `${logo.color}12`, display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      fontSize: 15, flexShrink: 0,
                      border: `1px solid ${logo.color}20`,
                    }}>{logo.emoji}</div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, color: COLORS.textPrimary, lineHeight: 1.3 }}>{logo.name || v || r.channelCode}</div>
                      {r.channelType && <span style={{ fontSize: 11, color: logo.color, fontWeight: 500 }}>{r.channelType}</span>}
                    </div>
                  </div>
                );
              },
            },
            { title: t('doc_colEntity'), dataIndex: 'entity', width: 110,
              render: v => v ? <span style={{ fontWeight: 500 }}>{v}</span> : <span style={{ color: '#d1d5db' }}>—</span>,
            },
            { title: t('doc_colKeyFunction'), dataIndex: 'keyFunction', width: 170, ellipsis: true,
              render: v => v ? <Tooltip title={v}><span>{v}</span></Tooltip> : <span style={{ color: '#d1d5db' }}>—</span>,
            },
            { title: t('doc_colRegion'), dataIndex: 'region', width: 80, align: 'center',
              render: v => v ? <span style={{ fontWeight: 500, color: COLORS.textSecondary }}>{v}</span> : <span style={{ color: '#d1d5db' }}>—</span>,
            },
            { title: t('doc_colCountry'), dataIndex: 'country', width: 90,
              render: v => v || <span style={{ color: '#d1d5db' }}>—</span>,
            },
            { title: t('doc_colCurrency'), dataIndex: 'currency', width: 120,
              render: v => {
                if (!v) return <span style={{ color: '#d1d5db' }}>—</span>;
                const currencies = v.split(',').map(c => c.trim());
                if (currencies.length <= 2) {
                  return <span>{currencies.map(c => `${getCurrencyIcon(c)} ${c}`).join(', ')}</span>;
                }
                return (
                  <Tooltip title={currencies.map(c => `${getCurrencyIcon(c)} ${c}`).join(', ')}>
                    <span>{getCurrencyIcon(currencies[0])} {currencies[0]}</span>
                    <span style={{ fontSize: 11, color: COLORS.accent, fontWeight: 600, marginLeft: 4, background: COLORS.accentBg, padding: '1px 6px', borderRadius: 8 }}>+{currencies.length - 1}</span>
                  </Tooltip>
                );
              },
            },
            { title: t('doc_colPaymentMethod'), dataIndex: 'paymentMethod', width: 120,
              render: v => v ? <span style={{ fontFamily: "'JetBrains Mono', 'SF Mono', monospace", fontSize: 12, color: '#475569' }}>{v}</span> : <span style={{ color: '#d1d5db' }}>—</span>,
            },
            { title: t('doc_colUserType'), dataIndex: 'businessType', width: 90, align: 'center',
              render: v => v ? <Tag color={bizColor[v] || 'default'} style={{ margin: 0, borderRadius: 12, fontSize: 11 }}>{v}</Tag> : <span style={{ color: '#d1d5db' }}>—</span>,
            },
            { title: t('doc_capabilities'), width: 300,
              render: (_, r) => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {capPill(t('doc_colOpenAccount'), r.needOpenAccount)}
                  {capPill(t('doc_colDeposit'), r.supportDeposit)}
                  {capPill(t('doc_colWithdraw'), r.supportWithdraw)}
                  {capPill(t('doc_colRefund'), r.supportRefund)}
                  {capPill(t('doc_colPartialRefund'), r.supportPartialRefund)}
                  {capPill(t('doc_colSameNameVa'), r.sameNameVa)}
                </div>
              ),
            },
            { title: '', key: 'action', width: 60, fixed: 'right', align: 'center',
              render: (_, r) => (
                <Button type="text" size="small" onClick={() => setDetailData(r)}
                  style={{ color: COLORS.accent, fontWeight: 500, fontSize: 12, padding: '2px 8px', borderRadius: 6 }}
                  icon={<RightOutlined style={{ fontSize: 10 }} />}
                />
              ),
            },
          ]}
        />
      </div>

      {/* Detail Modal — redesigned */}
      <Modal
        open={!!detailData} onCancel={() => setDetailData(null)} footer={null}
        width={620} destroyOnClose
        title={null} closable
        styles={{ body: { padding: 0 } }}
      >
        {detailData && (
          <div>
            {/* Modal header */}
            <div style={{
              padding: '24px 28px 20px', borderBottom: '1px solid #f0f2f5',
              display: 'flex', alignItems: 'center', gap: 16,
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: 12,
                background: `${getChannelLogo(detailData.channelCode).color}12`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22, border: `1px solid ${getChannelLogo(detailData.channelCode).color}20`,
              }}>{getChannelLogo(detailData.channelCode).emoji}</div>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary }}>{detailData.channelName || detailData.channelCode}</div>
                <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                  {detailData.channelType && <Tag color={typeColor[detailData.channelType]} style={{ borderRadius: 12, fontSize: 11 }}>{detailData.channelType}</Tag>}
                  {detailData.businessType && <Tag color={bizColor[detailData.businessType]} style={{ borderRadius: 12, fontSize: 11 }}>{detailData.businessType}</Tag>}
                </div>
              </div>
            </div>

            {/* Modal body */}
            <div style={{ padding: '20px 28px 28px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 24px', marginBottom: 24 }}>
                {[
                  [t('doc_colEntity'), detailData.entity],
                  [t('doc_colKeyFunction'), detailData.keyFunction],
                  [t('doc_colRegion'), detailData.region],
                  [t('doc_colCountry'), detailData.country],
                  [t('doc_colCurrency'), detailData.currency ? `${getCurrencyIcon(detailData.currency)} ${detailData.currency}` : null],
                  [t('doc_colPaymentMethod'), detailData.paymentMethod],
                ].map(([label, value], i) => (
                  <div key={i}>
                    <div style={{ fontSize: 11, fontWeight: 500, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 13.5, fontWeight: 500, color: value ? COLORS.textPrimary : '#d1d5db' }}>{value || '—'}</div>
                  </div>
                ))}
              </div>

              <div style={{ borderTop: '1px solid #f0f2f5', paddingTop: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 500, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 10 }}>{t('doc_capabilities')}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {capPill(t('doc_colOpenAccount'), detailData.needOpenAccount)}
                  {capPill(t('doc_colDeposit'), detailData.supportDeposit)}
                  {capPill(t('doc_colWithdraw'), detailData.supportWithdraw)}
                  {capPill(t('doc_colRefund'), detailData.supportRefund)}
                  {capPill(t('doc_colPartialRefund'), detailData.supportPartialRefund)}
                  {capPill(t('doc_colSameNameVa'), detailData.sameNameVa)}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      <style>{`
        .channel-matrix-table .ant-table-tbody > tr:last-child > td {
          border-bottom: none !important;
        }
      `}</style>
    </div>
  );
}

// ─── Copy JSON Button ───
function CopyJsonBtn({ data, t }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => { navigator.clipboard.writeText(JSON.stringify(data, null, 2)); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <Button size="small" type="text" icon={copied ? <CheckOutlined style={{ color: '#22c55e' }} /> : <CopyOutlined />} onClick={handleCopy} style={{ fontSize: 12, color: copied ? '#22c55e' : '#94a3b8' }}>
      {copied ? t('doc_copied') : t('doc_copy')}
    </Button>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT — Checkout.com-inspired 3-panel layout
// ═══════════════════════════════════════════════════════════════
export default function ApiDocumentation({ isPublic = false }) {
  const { t, language } = useLanguage();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedApiCode, setSelectedApiCode] = useState('ACCOUNT_CREATE');
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [metaLoading, setMetaLoading] = useState(false);
  const [userChannels, setUserChannels] = useState([]);
  const [metaCache, setMetaCache] = useState({});
  const [activeSection, setActiveSection] = useState('overview');
  const [webhookEventType, setWebhookEventType] = useState('ACCOUNT');
  const [corridorCountry, setCorridorCountry] = useState('');
  const [corridorCurrency, setCorridorCurrency] = useState('');
  const [corridorRequirements, setCorridorRequirements] = useState(null);
  const [corridorLoading, setCorridorLoading] = useState(false);
  const [selectedPayoutType, setSelectedPayoutType] = useState(null);

  // Country → primary currencies mapping
  const COUNTRY_CURRENCIES = {
    US: ['USD'], GB: ['GBP','EUR'], HK: ['HKD','USD'], SG: ['SGD','USD'],
    IN: ['INR'], JP: ['JPY'], AU: ['AUD'], CA: ['CAD'],
    DE: ['EUR'], FR: ['EUR'], CH: ['CHF','EUR'], NZ: ['NZD'],
    BR: ['BRL'], MX: ['MXN'], AE: ['AED'], SA: ['SAR'],
    ID: ['IDR'], TH: ['THB'], PH: ['PHP'], MY: ['MYR'],
    VN: ['VND'], KR: ['KRW'], CN: ['CNY'], NG: ['NGN'],
    KE: ['KES'], ZA: ['ZAR'], SE: ['SEK'], NO: ['NOK'],
    DK: ['DKK'], PL: ['PLN'], IL: ['ILS'], TR: ['TRY'],
  };
  const corridorCurrencyOptions = corridorCountry && COUNTRY_CURRENCIES[corridorCountry]
    ? COUNTRY_CURRENCIES[corridorCountry].map(c => ({ value: c, label: `${getCurrencyIcon(c)} ${c}` }))
    : [];

  const API_CODE_LABELS = useMemo(() => getApiCodeLabels(t), [language]);
  const SCENE_OPTIONS = useMemo(() => getSceneOptions(t), [language]);
  const COMMON_RESPONSE_FIELDS = useMemo(() => getCommonResponseFields(t), [language]);
  const ERROR_CODES = useMemo(() => getErrorCodes(t), [language]);
  const ERROR_CAT_CONFIG = useMemo(() => getErrorCatConfig(t), [language]);

  const isEn = language === 'en-US';
  const localizeFields = (fields) => {
    if (!isEn || !fields) return fields;
    return fields.map(f => ({ ...f, label: fieldLabelMap[f.label] || f.label, tip: f.tip ? (fieldTipMap[f.tip] || f.tip) : f.tip }));
  };

  /**
   * 过滤 tip 文本，只保留与选中渠道相关的部分
   * tip 格式: "EQUALS_MONEY/SGB必填; TAZAPAY按走廊动态要求; PYVIO仅USD必填"
   * 选中 TAZAPAY 后只显示 "TAZAPAY按走廊动态要求"
   * 如果 tip 是纯枚举值（A / B / C）或不含渠道名，原样返回
   */
  const filterTipByChannel = (tip, channel) => {
    if (!tip || !channel) return tip;
    // 纯枚举值（不含分号，只有 / 分隔的大写值）
    if (!tip.includes(';') && /^[A-Z_]+ \/ [A-Z_]/.test(tip)) return tip;
    // 按分号分割，找包含当前渠道名的片段
    if (tip.includes(';')) {
      const parts = tip.split(/;\s*/);
      const matched = parts.filter(p => p.toUpperCase().includes(channel.toUpperCase()));
      if (matched.length > 0) return matched.join('; ');
      // 没有匹配到任何渠道名的片段，可能是通用描述，原样返回
      return tip;
    }
    // 不含分号的单条 tip，如果包含其他渠道名但不包含当前渠道，隐藏
    // 检测是否包含渠道名模式（大写字母+下划线）
    const channelPattern = /\b[A-Z][A-Z_]+\b/g;
    const mentionedChannels = tip.match(channelPattern) || [];
    const knownChannels = ['EQUALS_MONEY', 'PAGSMILE', 'SGB', 'PYVIO', 'LEAN_TECH', 'KORAPAY', 'PALMPAY', 'BANK_FRICK', 'BANKING_CIRCLE', 'INSWITCH', 'TAZAPAY', 'BITSO'];
    const mentionsOtherChannel = mentionedChannels.some(m => knownChannels.includes(m) && m !== channel);
    const mentionsCurrentChannel = mentionedChannels.some(m => m === channel);
    if (mentionsOtherChannel && !mentionsCurrentChannel) return null;
    return tip;
  };

  const isChannelSpecific = selectedApiCode && CHANNEL_SPECIFIC_APIS.has(selectedApiCode);

  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (!initialized && overview) {
      const apiChannels = overview.channelsByApi?.['ACCOUNT_CREATE'] || [];
      const candidates = userChannels.length > 0 ? userChannels.filter(ch => apiChannels.includes(ch)) : apiChannels;
      if (candidates[0]) setSelectedChannel(candidates[0]);
      setInitialized(true);
    }
  }, [userChannels, overview, initialized]);

  useEffect(() => { loadOverview(); if (!isPublic) loadUserChannels(); }, []);

  const loadUserChannels = async () => {
    try {
      const { data } = await testApi.getChannelCurrencies();
      const responseData = data.data;
      const channelCurrencyMap = responseData?.responseBody || responseData || {};
      setUserChannels(Object.keys(channelCurrencyMap));
    } catch (e) { console.error('Failed to load user channels:', e); }
  };

  const loadOverview = async () => {
    try {
      const { data } = await metaApi.getOverview();
      setOverview(data);
      setMetaCache({});
    } catch (e) { console.error('Failed to load metadata overview:', e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (selectedChannel && selectedApiCode) loadMetadata(selectedApiCode, selectedChannel);
    else if (selectedChannel && !selectedApiCode) {
      const apiCode = Object.entries(overview?.channelsByApi || {}).find(([, chs]) => chs.includes(selectedChannel))?.[0];
      if (apiCode) loadMetadata(apiCode, selectedChannel);
      else setMetadata(null);
    } else setMetadata(null);
  }, [selectedApiCode, selectedChannel, overview]);

  const loadMetadata = async (apiCode, channelCode) => {
    const cacheKey = `${apiCode}|${channelCode}`;
    if (metaCache[cacheKey]) { setMetadata(metaCache[cacheKey]); return; }
    setMetaLoading(true);
    try {
      const { data } = await metaApi.get(apiCode, channelCode);
      setMetaCache(prev => ({ ...prev, [cacheKey]: data }));
      setMetadata(data);
    } catch (e) { setMetadata(null); }
    finally { setMetaLoading(false); }
  };

  const loadCorridorRequirements = async (country, currency) => {
    if (!country || country.length !== 2 || !currency || currency.length !== 3) return;
    setCorridorLoading(true);
    setSelectedPayoutType(null);
    try {
      const { data } = await metaApi.getPayoutRequirements('TAZAPAY', country, currency);
      const reqData = data?.data || null;
      setCorridorRequirements(reqData?.payoutMethods?.length > 0 ? reqData : null);
      // Auto-select first payout type
      if (reqData?.payoutMethods?.length > 0) {
        setSelectedPayoutType(reqData.payoutMethods[0].payoutType);
      }
    } catch (e) {
      console.error('Failed to load corridor requirements:', e);
      setCorridorRequirements(null);
    } finally {
      setCorridorLoading(false);
    }
  };

  const availableChannels = useMemo(() => {
    if (selectedApiCode && overview) {
      const apiChannels = overview.channelsByApi?.[selectedApiCode] || [];
      if (!userChannels.length) return apiChannels;
      return userChannels.filter(ch => apiChannels.includes(ch));
    }
    if (!userChannels.length && overview) {
      const allChannels = new Set();
      Object.values(overview.channelsByApi || {}).forEach(chs => chs.forEach(ch => allChannels.add(ch)));
      return [...allChannels];
    }
    return userChannels;
  }, [userChannels, overview, selectedApiCode]);

  const channelCount = availableChannels.length;
  const channelOptions = useMemo(() => buildChannelOptions(availableChannels), [availableChannels]);

  // ─── Checkout.com-style parameter row renderer ───
  const renderParamRow = (field) => {
    const f = field;
    const isEnum = f.type === 'ENUM' || (f.type === 'STRING' && f.tip && /^[A-Z_]+ \/ [A-Z_]/.test(f.tip?.replace(/^[^:]*:\s*/, '') || ''));
    const condRequired = !f.required && f.tip && /必填|[Rr]equired/.test(f.tip);
    const typeDisplay = isEnum ? 'enum' : (f.type || 'string').toLowerCase();
    const typeColors = { string: '#3b82f6', enum: '#8b5cf6', integer: '#f59e0b', long: '#f59e0b', decimal: '#f59e0b', boolean: '#22c55e', object: '#ec4899', array: '#06b6d4', json_string: '#8b5cf6', file: '#64748b' };

    // Split conditional tips by semicolons into structured items
    const renderTip = () => {
      if (!f.tip) return null;
      // Pure enum-like values: "A / B / C" (no semicolons, no colons, no commas)
      if (f.tip.includes(' / ') && !f.tip.includes(';') && !f.tip.includes(':') && !f.tip.includes(',')) {
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
            {f.tip.split(' / ').map((v, i) => (
              <span key={i} style={{ fontSize: 11, padding: '1px 6px', borderRadius: 3, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}>{v.trim()}</span>
            ))}
          </div>
        );
      }
      // Conditional-required tips with semicolons — yellow structured list
      if (condRequired && f.tip.includes(';')) {
        const parts = f.tip.split(/;\s*/);
        return (
          <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {parts.map((part, i) => (
              <div key={i} style={{
                fontSize: 11.5, color: '#92400e', background: '#fefce8',
                border: '1px solid #fde68a', borderRadius: 4, padding: '2px 8px',
                display: 'inline-flex', alignItems: 'center', gap: 4, width: 'fit-content',
              }}>
                <span style={{ color: '#d97706', fontSize: 10 }}>▸</span> {part.trim()}
              </div>
            ))}
          </div>
        );
      }
      // Conditional tip (single line, no semicolons)
      if (condRequired) {
        return <div style={{ marginTop: 4, fontSize: 11.5, color: '#92400e', background: '#fefce8', border: '1px solid #fde68a', borderRadius: 4, padding: '2px 8px', display: 'inline-flex', alignItems: 'center', gap: 4, width: 'fit-content' }}><span style={{ color: '#d97706', fontSize: 10 }}>▸</span> {f.tip}</div>;
      }
      // Non-conditional tips with semicolons — gray structured list (e.g. enum value ranges per channel)
      if (f.tip.includes(';')) {
        const parts = f.tip.split(/;\s*/);
        return (
          <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {parts.map((part, i) => {
              // If a part contains "/ " enum list, render as inline tags
              const enumMatch = part.match(/^(.+?):\s*(.+\/.+)$/);
              if (enumMatch) {
                const [, prefix, enumStr] = enumMatch;
                const vals = enumStr.split(/\s*\/\s*/);
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11.5, color: '#64748b', whiteSpace: 'nowrap' }}>{prefix.trim()}:</span>
                    {vals.map((v, j) => (
                      <span key={j} style={{ fontSize: 11, padding: '1px 6px', borderRadius: 3, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}>{v.trim()}</span>
                    ))}
                  </div>
                );
              }
              return (
                <div key={i} style={{ fontSize: 11.5, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 4, padding: '2px 8px', display: 'inline-flex', alignItems: 'center', gap: 4, width: 'fit-content' }}>
                  <span style={{ color: '#94a3b8', fontSize: 10 }}>▸</span> {part.trim()}
                </div>
              );
            })}
          </div>
        );
      }
      // Default: plain text
      return <div style={{ marginTop: 2, fontSize: 12, color: '#94a3b8' }}>{f.tip}</div>;
    };

    return (
      <div key={f.name} style={{ padding: '12px 0', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 24, alignItems: 'flex-start' }}>
        <div style={{ flex: '0 0 320px', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {f._isChild && <span style={{ color: COLORS.textMuted, fontSize: 10, marginRight: -2 }}>↳</span>}
            <code style={{ fontSize: 13, fontWeight: 500, color: f._isChild ? COLORS.textSecondary : COLORS.textPrimary, fontFamily: "'JetBrains Mono', 'SF Mono', monospace", background: '#f8fafc', padding: '1px 6px', borderRadius: 4, border: '1px solid #e2e8f0', whiteSpace: 'normal', wordBreak: 'break-all' }}>{f._isChild ? f.name.split('.').pop() : f.name}</code>
            {f.required && <span style={{ fontSize: 10, color: COLORS.red, fontWeight: 700, whiteSpace: 'nowrap' }}>required</span>}
            {condRequired && <span style={{ fontSize: 10, color: COLORS.orange, fontWeight: 600, whiteSpace: 'nowrap' }}>conditional</span>}
            {f._corridorRequired && <span style={{ fontSize: 10, color: '#fff', fontWeight: 600, whiteSpace: 'nowrap', background: COLORS.red, padding: '1px 5px', borderRadius: 3 }}>corridor required</span>}
            {f._corridorRecommended && <span style={{ fontSize: 10, color: '#fff', fontWeight: 600, whiteSpace: 'nowrap', background: COLORS.orange, padding: '1px 5px', borderRadius: 3 }}>corridor recommended</span>}
          </div>
          <div style={{ marginTop: 3, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: typeColors[typeDisplay] || '#64748b', fontWeight: 500 }}>{typeDisplay}</span>
            {f.maxLen && <span style={{ fontSize: 10, color: '#94a3b8' }}>{f.minLen ? `${f.minLen}~${f.maxLen}` : `≤${f.maxLen}`}</span>}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, color: COLORS.textSecondary, lineHeight: 1.6 }}>{f.label}</div>
          {f.options && f.options.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
              {f.options.map((v, i) => (
                <span key={i} style={{ fontSize: 11, padding: '1px 6px', borderRadius: 3, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}>{v}</span>
              ))}
            </div>
          )}
          {renderTip()}
        </div>
      </div>
    );
  };

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  const isAdmin = !isPublic && localStorage.getItem('userRole') === 'ADMIN';

  // ═══════════════════════════════════════════════════════════
  // OVERVIEW TAB — Checkout.com "Get started" style
  // ═══════════════════════════════════════════════════════════
  const overviewTab = (
    <div style={{ maxWidth: 1600, margin: '0 auto' }}>
      {/* Hero */}
      <div style={{ marginBottom: 40 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: COLORS.textPrimary, margin: '0 0 6px', letterSpacing: '-0.3px' }}>Fiat Payment API Reference</h1>
        <p style={{ fontSize: 13, color: COLORS.textSecondary, margin: 0, lineHeight: 1.6 }}>
          {t('doc_totalApis', Object.keys(API_CODE_LABELS).length)}
        </p>
      </div>

      {/* API Groups — Checkout.com endpoint list style */}
      {SCENE_OPTIONS.map((group) => (
        <div key={group.label} style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: COLORS.textPrimary }}>{group.label}</span>
            <span style={{ fontSize: 12, color: COLORS.textMuted, background: '#f1f5f9', padding: '1px 8px', borderRadius: 10 }}>{group.options.length}</span>
          </div>
          <div style={{ borderRadius: 8, border: `1px solid ${COLORS.border}`, overflow: 'hidden', background: '#fff' }}>
            {group.options.map((o, i) => {
              const api = API_CODE_LABELS[o.value];
              return (
                <div key={o.value}
                  onClick={() => { setSelectedApiCode(o.value); setActiveSection('detail'); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px',
                    borderBottom: i < group.options.length - 1 ? `1px solid ${COLORS.borderLight}` : 'none',
                    cursor: 'pointer', transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                >
                  <MethodBadge method={api.method} />
                  <code style={{ fontSize: 13, color: COLORS.textPrimary, fontFamily: "'JetBrains Mono', 'SF Mono', monospace", flex: 1 }}>{api.uri}</code>
                  <span style={{ fontSize: 13, color: COLORS.textSecondary, flex: '0 0 auto' }}>{api.label}</span>
                  <RightOutlined style={{ fontSize: 10, color: COLORS.textMuted }} />
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Common Response Structure */}
      <div style={{ marginBottom: 32 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: COLORS.textPrimary, margin: '0 0 16px' }}>{t('doc_commonResponse')}</h2>
        <p style={{ fontSize: 13.5, color: COLORS.textSecondary, margin: '0 0 16px', lineHeight: 1.6 }}>
          {t('doc_commonResponseDesc')} <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 3, fontSize: 12 }}>data</code> {t('doc_commonResponseDesc2')}
        </p>
        <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: '4px 20px' }}>
          {localizeFields(COMMON_RESPONSE_FIELDS).map(f => renderParamRow(f))}
        </div>
      </div>

      {/* Error Codes */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, color: COLORS.textPrimary, margin: 0 }}>{t('doc_errorCodeLookup')}</h2>
          <Space size={6}>
            {Object.entries(ERROR_CAT_CONFIG).map(([k, v]) => (
              <span key={k} style={{ fontSize: 10, padding: '1px 8px', borderRadius: 10, color: v.color, background: v.bg, border: `1px solid ${v.color}22`, fontWeight: 500 }}>{v.label}</span>
            ))}
          </Space>
        </div>
        <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
          <Table dataSource={ERROR_CODES} rowKey="code" size="small" pagination={false}
            columns={[
              { title: t('doc_colCategory'), dataIndex: 'cat', width: 90, align: 'center',
                render: v => { const c = ERROR_CAT_CONFIG[v]; return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, color: c.color, background: c.bg, fontWeight: 500 }}>{c.label}</span>; },
                onCell: (record, index) => {
                  const data = ERROR_CODES;
                  if (index === 0 || data[index].cat !== data[index - 1].cat) {
                    let span = 1;
                    for (let i = index + 1; i < data.length && data[i].cat === record.cat; i++) span++;
                    return { rowSpan: span };
                  }
                  return { rowSpan: 0 };
                },
              },
              { title: 'subCode', dataIndex: 'code', width: 120, render: (v, r) => <code style={{ color: ERROR_CAT_CONFIG[r.cat].color, fontWeight: 600, fontSize: 12 }}>{v}</code> },
              { title: t('doc_colDesc'), dataIndex: 'desc' },
              { title: t('doc_colAction'), dataIndex: 'action', render: v => <span style={{ fontSize: 12, color: COLORS.textSecondary }}>{v}</span> },
            ]}
          />
        </div>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════
  // DETAIL TAB — Checkout.com 3-panel: left params + right code
  // ═══════════════════════════════════════════════════════════
  const detailTab = (() => {
    const apiInfo = API_CODE_LABELS[selectedApiCode] || {};
    const baseParams = BASE_PARAMS[selectedApiCode] || [];
    const responseParams = RESPONSE_PARAMS[selectedApiCode] || [];
    const examples = isChannelSpecific ? null : API_EXAMPLES[selectedApiCode];
    const exampleRequest = isChannelSpecific ? metadata?.exampleRequest : examples?.request;
    const exampleResponse = isChannelSpecific ? metadata?.exampleResponse : examples?.response;

    return (
      <div style={{ maxWidth: 1600, margin: '0 auto' }}>
        {/* Top bar: API selector + channel selector */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '12px 24px',
          background: '#fff', borderBottom: `1px solid ${COLORS.border}`, position: 'sticky', top: 0, zIndex: 10,
        }}>
          <Select value={selectedApiCode} onChange={v => {
            setSelectedApiCode(v); setMetadata(null);
            if (v && !CHANNEL_SPECIFIC_APIS.has(v)) setSelectedChannel(null);
            else if (v && CHANNEL_SPECIFIC_APIS.has(v)) {
              const apiChannels = overview?.channelsByApi?.[v] || [];
              const available = userChannels.length > 0 ? userChannels.filter(ch => apiChannels.includes(ch)) : apiChannels;
              if (!selectedChannel || !available.includes(selectedChannel)) {
                const preferred = v === 'DEPOSIT_CLOSE' && available.includes('CVPAY') ? 'CVPAY' : available[0] || null;
                setSelectedChannel(preferred);
              }
            }
          }} style={{ minWidth: 240 }} size="middle" options={SCENE_OPTIONS} variant="borderless" popupMatchSelectWidth={300} />
          {(!selectedApiCode || isChannelSpecific) && (
            <>
              <div style={{ width: 1, height: 20, background: COLORS.border }} />
              <Select value={selectedChannel} onChange={setSelectedChannel} style={{ minWidth: 200 }} size="middle" allowClear showSearch={false}
                placeholder={t('doc_selectChannel')} options={channelOptions} variant="borderless" popupMatchSelectWidth={260} />
              <span style={{ fontSize: 11, color: COLORS.textMuted }}>{t('doc_channelCount', channelCount)}</span>
            </>
          )}
        </div>

        {!selectedApiCode ? (
          <Empty description={t('doc_selectApi')} style={{ padding: 80 }} />
        ) : (
          /* ─── Three-panel layout ─── */
          <div style={{ display: 'flex', minHeight: 'calc(100vh - 180px)' }}>
            {/* LEFT: Documentation */}
            <div style={{ flex: 1, padding: '28px 32px', overflow: 'auto', borderRight: `1px solid ${COLORS.borderLight}`, minWidth: 0 }}>
              {/* Endpoint header */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <MethodBadge method={apiInfo.method} />
                  <code style={{ fontSize: 15, fontWeight: 500, color: COLORS.textPrimary, fontFamily: "'JetBrains Mono', 'SF Mono', monospace" }}>{apiInfo.uri}</code>
                </div>
                <p style={{ fontSize: 14, color: COLORS.textSecondary, margin: 0 }}>{apiInfo.desc}</p>
              </div>

              {/* Channel selector for channel-specific APIs */}
              {isChannelSpecific && selectedChannel && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ padding: '8px 12px', background: COLORS.accentBg, borderRadius: 6, border: `1px solid ${COLORS.accent}22`, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 12, color: COLORS.accent, fontWeight: 500 }}>
                      {(() => { const logo = getChannelLogo(selectedChannel); return `${logo.emoji} ${selectedChannel}`; })()}
                    </span>
                  </div>
                  {metadata?.description && (
                    <div style={{ marginTop: 8, fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.6 }}>
                      {metadata.description}
                    </div>
                  )}
                </div>
              )}

              {/* Corridor Requirements Query Panel (TAZAPAY CARD_BIND only) */}
              {selectedChannel === 'TAZAPAY' && selectedApiCode === 'CARD_BIND' && (
                <div style={{ marginBottom: 24, borderRadius: 8, border: `1px solid ${COLORS.accent}22`, overflow: 'hidden' }}>
                  {/* Header */}
                  <div style={{ padding: '12px 20px', background: COLORS.accentBg, borderBottom: `1px solid ${COLORS.accent}22`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 14 }}>🔍</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.textPrimary }}>{t('doc_corridorTitle')}</span>
                    </div>
                    {corridorLoading && <Spin size="small" />}
                  </div>
                  {/* Selectors */}
                  <div style={{ padding: '16px 20px' }}>
                    <div style={{ fontSize: 12, color: COLORS.textSecondary, marginBottom: 12 }}>{t('doc_corridorDesc')}</div>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4, fontWeight: 500 }}>{t('doc_corridorCountry')}</div>
                        <Select
                          showSearch placeholder={t('doc_corridorSelectCountry')} value={corridorCountry || undefined}
                          onChange={v => {
                            setCorridorCountry(v);
                            const currencies = v ? (COUNTRY_CURRENCIES[v] || []) : [];
                            if (currencies.length === 1) {
                              setCorridorCurrency(currencies[0]);
                              loadCorridorRequirements(v, currencies[0]);
                            } else {
                              setCorridorCurrency('');
                              setCorridorRequirements(null);
                              setSelectedPayoutType(null);
                            }
                          }}
                          style={{ width: 160 }} allowClear size="middle"
                          filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                          options={[
                            {value:'US',label:'🇺🇸 US'},{value:'GB',label:'🇬🇧 GB'},{value:'HK',label:'🇭🇰 HK'},{value:'SG',label:'🇸🇬 SG'},
                            {value:'IN',label:'🇮🇳 IN'},{value:'JP',label:'🇯🇵 JP'},{value:'AU',label:'🇦🇺 AU'},{value:'CA',label:'🇨🇦 CA'},
                            {value:'DE',label:'🇩🇪 DE'},{value:'FR',label:'🇫🇷 FR'},{value:'CH',label:'🇨🇭 CH'},{value:'NZ',label:'🇳🇿 NZ'},
                            {value:'BR',label:'🇧🇷 BR'},{value:'MX',label:'🇲🇽 MX'},{value:'AE',label:'🇦🇪 AE'},{value:'SA',label:'🇸🇦 SA'},
                            {value:'ID',label:'🇮🇩 ID'},{value:'TH',label:'🇹🇭 TH'},{value:'PH',label:'🇵🇭 PH'},{value:'MY',label:'🇲🇾 MY'},
                            {value:'VN',label:'🇻🇳 VN'},{value:'KR',label:'🇰🇷 KR'},{value:'CN',label:'🇨🇳 CN'},{value:'NG',label:'🇳🇬 NG'},
                            {value:'KE',label:'🇰🇪 KE'},{value:'ZA',label:'🇿🇦 ZA'},{value:'SE',label:'🇸🇪 SE'},{value:'NO',label:'🇳🇴 NO'},
                            {value:'DK',label:'🇩🇰 DK'},{value:'PL',label:'🇵🇱 PL'},{value:'IL',label:'🇮🇱 IL'},{value:'TR',label:'🇹🇷 TR'},
                          ]}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4, fontWeight: 500 }}>{t('doc_corridorCurrency')}</div>
                        <Select
                          showSearch placeholder={corridorCountry ? t('doc_corridorSelectCurrency') : t('doc_corridorSelectCountryFirst')}
                          value={corridorCurrency || undefined}
                          onChange={v => { setCorridorCurrency(v); if (corridorCountry && v) loadCorridorRequirements(corridorCountry, v); }}
                          style={{ width: 140 }} allowClear disabled={!corridorCountry} size="middle"
                          filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                          options={corridorCurrencyOptions}
                        />
                      </div>
                    </div>

                    {/* Results */}
                    {corridorRequirements?.payoutMethods?.length > 0 && (
                      <div style={{ marginTop: 16 }}>
                        {/* Payout Type Selector */}
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 500 }}>{t('doc_corridorPayoutType') || 'Payout Type'}</div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            {corridorRequirements.payoutMethods.map(pm => {
                              const isSelected = selectedPayoutType === pm.payoutType;
                              const baseColor = pm.payoutType === 'swift' ? COLORS.blue : COLORS.green;
                              return (
                                <div
                                  key={pm.payoutType}
                                  onClick={() => setSelectedPayoutType(pm.payoutType)}
                                  style={{
                                    padding: '6px 16px', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 12,
                                    background: isSelected ? baseColor : '#fff',
                                    color: isSelected ? '#fff' : baseColor,
                                    border: `1.5px solid ${baseColor}`,
                                    transition: 'all 0.2s',
                                    boxShadow: isSelected ? `0 2px 8px ${baseColor}40` : 'none',
                                  }}
                                >
                                  {pm.payoutType.toUpperCase()}
                                  {pm.transferLimit && (
                                    <span style={{ fontSize: 10, marginLeft: 6, opacity: 0.8 }}>
                                      {pm.transferLimit.minimum}~{pm.transferLimit.maximum}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        {/* Selected payout type details */}
                        {selectedPayoutType && (() => {
                          const pm = corridorRequirements.payoutMethods.find(m => m.payoutType === selectedPayoutType);
                          if (!pm) return null;
                          const baseColor = pm.payoutType === 'swift' ? COLORS.blue : COLORS.green;
                          return (
                            <div style={{ padding: '14px 16px', background: '#fff', borderRadius: 8, border: `1.5px solid ${baseColor}30` }}>
                              {/* Required fields */}
                              {(pm.requiredFields?.mainFields?.length > 0 || pm.requiredFields?.extParamFields?.length > 0) && (
                                <div style={{ marginBottom: 8 }}>
                                  <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.red, marginBottom: 4 }}>● {t('doc_corridorRequired')}</div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, paddingLeft: 12 }}>
                                    {(pm.requiredFields.mainFields || []).map(f => <Tag key={f} color="error" style={{ fontSize: 11, margin: 0 }}>{f}</Tag>)}
                                    {(pm.requiredFields.extParamFields || []).map(f => <Tag key={f} color="error" style={{ fontSize: 11, margin: 0, borderStyle: 'dashed' }}>ext.{f}</Tag>)}
                                  </div>
                                </div>
                              )}
                              {/* Recommended fields */}
                              {(pm.recommendedFields?.mainFields?.length > 0 || pm.recommendedFields?.extParamFields?.length > 0) && (
                                <div style={{ marginBottom: 8 }}>
                                  <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.orange, marginBottom: 4 }}>○ {t('doc_corridorRecommended')}</div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, paddingLeft: 12 }}>
                                    {(pm.recommendedFields.mainFields || []).map(f => <Tag key={f} color="warning" style={{ fontSize: 11, margin: 0 }}>{f}</Tag>)}
                                    {(pm.recommendedFields.extParamFields || []).map(f => <Tag key={f} color="warning" style={{ fontSize: 11, margin: 0, borderStyle: 'dashed' }}>ext.{f}</Tag>)}
                                  </div>
                                </div>
                              )}
                              {/* Networks */}
                              {pm.fundTransferNetworks?.length > 0 && (
                                <div>
                                  <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.blue, marginBottom: 4 }}>◆ {t('doc_corridorNetworks')}</div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, paddingLeft: 12 }}>
                                    {pm.fundTransferNetworks.map(n => (
                                      <Tooltip key={n.name} title={n.description}>
                                        <Tag color="processing" style={{ fontSize: 11, margin: 0, cursor: 'help' }}>{n.name}{n.transferLimit ? ` (${n.transferLimit.minimum}~${n.transferLimit.maximum})` : ''}</Tag>
                                      </Tooltip>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Request Parameters */}
              <div style={{ marginBottom: 28 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 600, color: COLORS.textPrimary, margin: 0 }}>{t('doc_requestParams')}</h3>
                  <span style={{ fontSize: 11, color: COLORS.textMuted, background: '#f1f5f9', padding: '1px 8px', borderRadius: 10 }}>{baseParams.length}</span>
                </div>
                <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: '0 20px' }}>
                  {localizeFields(baseParams).map(f => {
                    let field = f;
                    // 选中渠道时，过滤 tip 只显示该渠道相关的描述
                    if (selectedChannel && f.tip) {
                      field = { ...f, tip: filterTipByChannel(f.tip, selectedChannel) };
                    }
                    // TAZAPAY corridor: mark main fields as dynamically required/recommended
                    if (selectedChannel === 'TAZAPAY' && selectedPayoutType && corridorRequirements) {
                      const pm = corridorRequirements.payoutMethods?.find(m => m.payoutType === selectedPayoutType);
                      if (pm) {
                        const isReq = (pm.requiredFields?.mainFields || []).includes(f.name);
                        const isRec = (pm.recommendedFields?.mainFields || []).includes(f.name);
                        if (isReq) field = { ...field, _corridorRequired: true };
                        if (isRec) field = { ...field, _corridorRecommended: true };
                      }
                    }
                    return renderParamRow(field);
                  })}
                </div>
              </div>

              {/* extParam diff fields (channel-specific) */}
              {isChannelSpecific && (
                <Spin spinning={metaLoading}>
                  <div style={{ marginBottom: 28 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                      <h3 style={{ fontSize: 15, fontWeight: 600, color: COLORS.textPrimary, margin: 0 }}>{t('doc_extParamFields')}</h3>
                      <span style={{ fontSize: 11, color: COLORS.textMuted, background: '#f1f5f9', padding: '1px 8px', borderRadius: 10 }}>{(metadata?.fields || []).length}</span>
                    </div>
                    {(metadata?.fields || []).length > 0 ? (
                      <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: '0 20px' }}>
                        {localizeFields(metadata.fields).map(f => {
                          let field = f;
                          // TAZAPAY corridor: mark ext fields as dynamically required/recommended
                          if (selectedChannel === 'TAZAPAY' && selectedPayoutType && corridorRequirements) {
                            const pm = corridorRequirements.payoutMethods?.find(m => m.payoutType === selectedPayoutType);
                            if (pm) {
                              const isReq = (pm.requiredFields?.extParamFields || []).includes(f.name);
                              const isRec = (pm.recommendedFields?.extParamFields || []).includes(f.name);
                              if (isReq) field = { ...field, _corridorRequired: true };
                              if (isRec) field = { ...field, _corridorRecommended: true };
                            }
                          }
                          return renderParamRow(field);
                        })}
                      </div>
                    ) : (
                      <div style={{ padding: '20px 0', color: COLORS.textMuted, fontSize: 13 }}>{metadata ? t('doc_noExtParam') : t('doc_noConfig')}</div>
                    )}
                  </div>
                </Spin>
              )}

              {/* Response Parameters */}
              {responseParams.length > 0 && (
                <div style={{ marginBottom: 28 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                    <h3 style={{ fontSize: 15, fontWeight: 600, color: COLORS.textPrimary, margin: 0 }}>{t('doc_responseParams')}</h3>
                    <span style={{ fontSize: 11, color: COLORS.textMuted, background: '#f1f5f9', padding: '1px 8px', borderRadius: 10 }}>data: {responseParams.length}</span>
                  </div>
                  <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: '0 20px' }}>
                    {localizeFields(responseParams).flatMap(f => {
                      const rows = [renderParamRow(f)];
                      if (f.children && f.children.length > 0) {
                        // Check if children have _channel grouping
                        const hasChannelGroups = f.children.some(c => c._channel);
                        if (hasChannelGroups) {
                          // Group children by _channel
                          const groups = {};
                          const ungrouped = [];
                          f.children.forEach(c => {
                            if (c._channel) {
                              if (!groups[c._channel]) groups[c._channel] = [];
                              groups[c._channel].push(c);
                            } else {
                              ungrouped.push(c);
                            }
                          });
                          rows.push(
                            <div key={`${f.name}_children`} style={{ paddingLeft: 24, marginLeft: 12, marginBottom: 4 }}>
                              {Object.entries(groups).map(([channel, fields]) => {
                                const logo = getChannelLogo(channel);
                                return (
                                  <div key={channel} style={{ marginBottom: 8 }}>
                                    <div style={{
                                      display: 'inline-flex', alignItems: 'center', gap: 6,
                                      padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                                      background: `${logo.color}12`, color: logo.color,
                                      border: `1px solid ${logo.color}30`, marginBottom: 2, marginTop: 4,
                                    }}>
                                      {logo.emoji} {channel}
                                    </div>
                                    <div style={{ borderLeft: `2px solid ${logo.color}40`, paddingLeft: 12 }}>
                                      {localizeFields(fields).map(child => renderParamRow({ ...child, name: `${f.name}.${child.name}`, _isChild: true }))}
                                    </div>
                                  </div>
                                );
                              })}
                              {ungrouped.length > 0 && (
                                <div style={{ borderLeft: `2px solid ${COLORS.accent}22`, paddingLeft: 12 }}>
                                  {localizeFields(ungrouped).map(child => renderParamRow({ ...child, name: `${f.name}.${child.name}`, _isChild: true }))}
                                </div>
                              )}
                            </div>
                          );
                        } else {
                          rows.push(
                            <div key={`${f.name}_children`} style={{ paddingLeft: 24, borderLeft: `2px solid ${COLORS.accent}22`, marginLeft: 12, marginBottom: 4 }}>
                              {localizeFields(f.children).flatMap(child => {
                                const childRows = [renderParamRow({ ...child, name: `${f.name}.${child.name}`, _isChild: true })];
                                // Render grandchildren (2nd level nesting)
                                if (child.children && child.children.length > 0) {
                                  childRows.push(
                                    <div key={`${f.name}.${child.name}_children`} style={{ paddingLeft: 24, borderLeft: `2px solid ${COLORS.accent}11`, marginLeft: 12, marginBottom: 4 }}>
                                      {localizeFields(child.children).map(gc => renderParamRow({ ...gc, name: `${f.name}.${child.name}.${gc.name}`, _isChild: true }))}
                                    </div>
                                  );
                                }
                                return childRows;
                              })}
                            </div>
                          );
                        }
                      }
                      return rows;
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT: Code examples (dark panel) */}
            <div style={{
              flex: '0 0 420px', maxWidth: 420, background: COLORS.codeBg, padding: '28px 20px',
              overflow: 'auto', minHeight: '100%',
            }}>
              <div style={{ position: 'sticky', top: 0 }}>
                {exampleRequest && <DarkCodePanel title={t('doc_exampleRequest')} data={exampleRequest} t={t} />}
                {exampleResponse && <DarkCodePanel title={t('doc_exampleResponse')} data={exampleResponse} t={t} />}
                {!exampleRequest && !exampleResponse && (
                  <div style={{ padding: '40px 20px', textAlign: 'center', color: '#475569' }}>
                    <CodeOutlined style={{ fontSize: 32, marginBottom: 12, color: '#334155' }} />
                    <div style={{ fontSize: 13 }}>{isChannelSpecific && !selectedChannel ? t('doc_selectChannel') : t('doc_noConfig')}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  })();

  // ═══════════════════════════════════════════════════════════
  // WEBHOOK TAB
  // ═══════════════════════════════════════════════════════════
  const WEBHOOK_EVENT_DATA = {
    ACCOUNT: [
      { name: 'accountId', type: 'String', required: true, desc: '账户ID' },
      { name: 'status', type: 'String', required: true, desc: 'PROCESSING / ACTIVE / FAILED / CLOSED / DENIED' },
      { name: 'subStatus', type: 'String', required: true, desc: 'INIT / PRE_CHECKING / CREATING_CHANNEL / WAITING_CONNECT / SUCCESS / FAILED' },
      { name: 'externalUserId', type: 'String', required: true, desc: '业务线用户ID' },
      { name: 'channelCode', type: 'String', required: true, desc: '渠道' },
      { name: 'paymentMethod', type: 'String', required: true, desc: '支付方式' },
      { name: 'currency', type: 'String', required: true, desc: '币种' },
      { name: 'errorCode', type: 'String', required: false, desc: '失败时返回错误码' },
      { name: 'errorMsg', type: 'String', required: false, desc: '失败时返回错误信息' },
      { name: 'createTime', type: 'Long', required: true, desc: '创建时间' },
      { name: 'updateTime', type: 'Long', required: true, desc: '更新时间' },
      { name: 'accountName', type: 'String', required: true, desc: '账户名' },
      { name: 'accountNumber', type: 'String', required: true, desc: 'IBAN 或 accountNumber' },
      { name: 'bic', type: 'String', required: false, desc: 'SWIFT Code' },
      { name: 'bankName', type: 'String', required: false, desc: '银行名称' },
      { name: 'bankCountry', type: 'String', required: false, desc: '银行所在国家' },
      { name: 'bankAddress', type: 'String', required: false, desc: '银行地址' },
      { name: 'currencies', type: 'List<String>', required: false, desc: 'TAZAPAY: 多币种账户支持的币种列表，如 ["USD","EUR","GBP"]。currency="Multi" 时返回' },
      { name: 'multiCurrency', type: 'Boolean', required: false, desc: 'TAZAPAY: 是否多币种账户。true=多币种(currency="Multi")，false=单币种' },
      { name: 'extParam', type: 'JsonString', required: false, desc: '扩展参数', children: [
        { name: 'extParam.customerId', type: 'String', required: true, desc: 'LEAN_TECH: 渠道用户ID' },
        { name: 'extParam.accessToken', type: 'String', required: true, desc: 'LEAN_TECH: Access Token' },
        { name: 'extParam.channelAppToken', type: 'String', required: true, desc: 'LEAN_TECH: Channel App Token' },
        { name: 'extParam.paymentDestinationId', type: 'String', required: true, desc: 'LEAN_TECH: 支付目的地ID' },
        { name: 'extParam.fullNameRetrieved', type: 'String', required: false, desc: 'LEAN_TECH: 从银行获取的用户全名称' },
        { name: 'extParam.nameMatchType', type: 'String', required: false, desc: 'LEAN_TECH: 同名校验结果 PERFECT_MATCH / PARTIAL_MATCH / NO_MATCH / RECONNECT_REQUIRED / NO_RESULT' },
      ] },
    ],
    BANK_ACCOUNT: [
      { name: 'recipientId', type: 'String', required: true, desc: '绑卡ID' },
      { name: 'status', type: 'String', required: true, desc: 'PENDING / SUCCESS / FAIL' },
      { name: 'channelCode', type: 'String', required: true, desc: '渠道' },
      { name: 'externalUserId', type: 'String', required: false, desc: '业务线用户ID' },
      { name: 'accountName', type: 'String', required: true, desc: '账户姓名' },
      { name: 'bic', type: 'String', required: false, desc: 'SWIFT Code' },
      { name: 'iban', type: 'String', required: false, desc: 'IBAN' },
      { name: 'accountNumber', type: 'String', required: true, desc: '银行户名' },
      { name: 'bankName', type: 'String', required: false, desc: '银行名字' },
      { name: 'currency', type: 'String', required: true, desc: '币种' },
      { name: 'bankCountry', type: 'String', required: false, desc: '银行国家 (ISO 3166-1 alpha-3)' },
      { name: 'errorCode', type: 'String', required: false, desc: '失败时返回错误码' },
      { name: 'errorMsg', type: 'String', required: false, desc: '失败时返回错误信息' },
    ],
    DEPOSIT: [
      { name: 'externalOrderId', type: 'String', required: true, desc: '外部订单号' },
      { name: 'orderId', type: 'String', required: true, desc: '订单ID' },
      { name: 'status', type: 'String', required: true, desc: 'PROCESSING / SUCCESS / FAILED' },
      { name: 'subStatus', type: 'String', required: true, desc: '子状态' },
      { name: 'channelCode', type: 'String', required: true, desc: '渠道' },
      { name: 'depositMethod', type: 'String', required: true, desc: 'VIRTUAL_ACCOUNT / PIX / VIETQR / ZEN_WALLET / INTERACE_REQUEST' },
      { name: 'currency', type: 'String', required: true, desc: '币种' },
      { name: 'amount', type: 'String', required: true, desc: '订单金额' },
      { name: 'fee', type: 'String', required: true, desc: '手续费' },
      { name: 'createTime', type: 'Long', required: true, desc: '创建时间' },
      { name: 'updateTime', type: 'Long', required: true, desc: '更新时间' },
      { name: 'completedTime', type: 'Long', required: false, desc: '完成时间' },
      { name: 'errorCode', type: 'String', required: false, desc: '错误码' },
      { name: 'errorMsg', type: 'String', required: false, desc: '错误信息' },
      { name: 'senderInfo', type: 'Object', required: false, desc: 'Payer 信息', children: [
        { name: 'senderInfo.senderAcctName', type: 'String', required: true, desc: '发送方账户名称' },
        { name: 'senderInfo.senderAcctNo', type: 'String', required: false, desc: '发送方账户号' },
        { name: 'senderInfo.senderBic', type: 'String', required: false, desc: '发送方 SWIFT/BIC Code' },
        { name: 'senderInfo.senderAddress', type: 'String', required: false, desc: '发送方地址' },
        { name: 'senderInfo.senderCountryCode', type: 'String', required: false, desc: '发送方国家代码' },
        { name: 'senderInfo.purpose', type: 'String', required: false, desc: '充值意图' },
        { name: 'senderInfo.remarks', type: 'String', required: false, desc: '备注' },
        { name: 'senderInfo.tranTime', type: 'LocalDateTime', required: false, desc: '转账时间' },
      ] },
      { name: 'paymentInfo', type: 'Object', required: false, desc: '支付信息', children: [
        { name: 'paymentInfo.payType', type: 'String', required: true, desc: 'URL / QRCODE / AUTHORIZATION_ID' },
        { name: 'paymentInfo.payUrl', type: 'String', required: false, desc: '支付链接' },
        { name: 'paymentInfo.qrCode', type: 'String', required: false, desc: 'QR Code' },
        { name: 'paymentInfo.qrPicture', type: 'String', required: false, desc: '二维码图片' },
        { name: 'paymentInfo.authorizationId', type: 'String', required: false, desc: '授权ID (AUTHORIZATION_ID时有值)' },
        { name: 'paymentInfo.authorizationToken', type: 'String', required: false, desc: '授权Token (AUTHORIZATION_ID时有值)' },
        { name: 'paymentInfo.authorizationAppToken', type: 'String', required: false, desc: '前端授权Token (AUTHORIZATION_ID时有值)' },
        { name: 'paymentInfo.connectType', type: 'String', required: false, desc: 'REVERSE_ENGINEERING / OPEN_BANKING' },
      ] },
    ],
    WITHDRAW: [
      { name: 'externalOrderId', type: 'String', required: true, desc: '外部订单号' },
      { name: 'orderId', type: 'String', required: true, desc: '订单ID' },
      { name: 'status', type: 'String', required: true, desc: 'PROCESSING / SUCCESS / FAILED' },
      { name: 'subStatus', type: 'String', required: true, desc: 'INIT / PRE_CHECKING / CREATING_CHANNEL_ORDER / WAITING_CHANNEL_CALLBACK / SUCCESS / FAILED' },
      { name: 'channelCode', type: 'String', required: true, desc: '渠道' },
      { name: 'withdrawMethod', type: 'String', required: true, desc: 'BANK_TRANSFER / PIX_CODE / INTERAC / EFT' },
      { name: 'currency', type: 'String', required: true, desc: '币种' },
      { name: 'amount', type: 'String', required: true, desc: '订单金额' },
      { name: 'fee', type: 'String', required: true, desc: '手续费' },
      { name: 'feeCurrency', type: 'String', required: true, desc: '手续费币种' },
      { name: 'createTime', type: 'Long', required: true, desc: '创建时间' },
      { name: 'updateTime', type: 'Long', required: true, desc: '更新时间' },
      { name: 'completedTime', type: 'Long', required: false, desc: '完成时间' },
      { name: 'errorCode', type: 'String', required: false, desc: '错误码' },
      { name: 'errorMsg', type: 'String', required: false, desc: '错误信息' },
    ],
    REFUND: [
      { name: 'externalOrderId', type: 'String', required: true, desc: '退款单号' },
      { name: 'orderId', type: 'String', required: true, desc: '退款单ID' },
      { name: 'status', type: 'String', required: true, desc: 'INIT / PENDING / SUCCESS / FAILED' },
      { name: 'errorCode', type: 'String', required: false, desc: '错误码' },
      { name: 'errorMsg', type: 'String', required: false, desc: '错误信息' },
      { name: 'refundReason', type: 'String', required: false, desc: '退款原因' },
      { name: 'amount', type: 'String', required: true, desc: '原订单金额' },
      { name: 'arrivalAmount', type: 'String', required: false, desc: '退款到账金额' },
      { name: 'currency', type: 'String', required: true, desc: '币种' },
      { name: 'channelCode', type: 'String', required: true, desc: '渠道' },
      { name: 'paymentMethod', type: 'String', required: true, desc: '支付方式' },
      { name: 'createTime', type: 'Long', required: true, desc: '创建时间' },
      { name: 'completedTime', type: 'Long', required: false, desc: '完成时间' },
    ],
    USER: [
      { name: 'userId', type: 'String', required: true, desc: '用户ID' },
      { name: 'clientId', type: 'String', required: true, desc: '渠道 ClientID' },
      { name: 'status', type: 'String', required: true, desc: 'SUCCESS / PROCESSING / FAILED / DENIED' },
      { name: 'externalUserId', type: 'String', required: true, desc: '商户用户ID' },
      { name: 'channelUserId', type: 'String', required: true, desc: '渠道用户ID' },
      { name: 'failedReason', type: 'String', required: false, desc: '失败原因' },
    ],
  };

  const webhookEventOptions = [
    { value: 'ACCOUNT', label: `ACCOUNT - ${t('doc_webhookEventAccount')}`, color: '#1677ff' },
    { value: 'BANK_ACCOUNT', label: `BANK_ACCOUNT - ${t('doc_webhookEventCard')}`, color: '#52c41a' },
    { value: 'DEPOSIT', label: `DEPOSIT - ${t('doc_webhookEventDeposit')}`, color: '#fa8c16' },
    { value: 'WITHDRAW', label: `WITHDRAW - ${t('doc_webhookEventWithdraw')}`, color: '#ff4d4f' },
    { value: 'REFUND', label: `REFUND - ${t('doc_webhookEventRefund')}`, color: '#722ed1' },
    { value: 'USER', label: `USER - ${t('doc_webhookEventUser')}`, color: '#13c2c2' },
  ];

  const localizeWebhookDesc = (desc) => {
    if (language === 'zh-CN') return desc;
    const map = { '账户ID': 'Account ID', '业务线用户ID': 'External User ID', '渠道': 'Channel Code', '支付方式': 'Payment Method', '币种': 'Currency', '失败时返回错误码': 'Error code on failure', '失败时返回错误信息': 'Error message on failure', '创建时间': 'Create Time', '更新时间': 'Update Time', '账户名': 'Account Name', '银行名称': 'Bank Name', '银行所在国家': 'Bank Country', '银行地址': 'Bank Address', '扩展参数': 'Extension Parameters', '绑卡ID': 'Recipient ID', '账户姓名': 'Account Name', '银行户名': 'Account Number', '银行名字': 'Bank Name', '银行国家 (ISO 3166-1 alpha-3)': 'Bank Country (ISO 3166-1 alpha-3)', '外部订单号': 'External Order ID', '订单ID': 'Order ID', '子状态': 'Sub Status', '订单金额': 'Amount', '手续费': 'Fee', '完成时间': 'Completed Time', '错误码': 'Error Code', '错误信息': 'Error Message', '手续费币种': 'Fee Currency', '退款单号': 'Refund Order ID', '退款单ID': 'Refund ID', '退款原因': 'Refund Reason', '原订单金额': 'Original Amount', '退款到账金额': 'Refund Arrival Amount', '用户ID': 'User ID', '商户用户ID': 'Merchant User ID', '渠道用户ID': 'Channel User ID', '失败原因': 'Failed Reason', 'LEAN_TECH: 渠道用户ID': 'LEAN_TECH: Channel User ID', 'LEAN_TECH: Access Token': 'LEAN_TECH: Access Token', 'LEAN_TECH: Channel App Token': 'LEAN_TECH: Channel App Token', 'LEAN_TECH: 支付目的地ID': 'LEAN_TECH: Payment Destination ID', 'LEAN_TECH: 从银行获取的用户全名称': 'LEAN_TECH: Full name retrieved from bank', 'LEAN_TECH: 同名校验结果 PERFECT_MATCH / PARTIAL_MATCH / NO_MATCH / RECONNECT_REQUIRED / NO_RESULT': 'LEAN_TECH: Name match result', '发送方账户名称': 'Sender Account Name', '发送方账户号': 'Sender Account No.', '发送方 SWIFT/BIC Code': 'Sender SWIFT/BIC Code', '发送方地址': 'Sender Address', '发送方国家代码': 'Sender Country Code', '充值意图': 'Deposit Purpose', '备注': 'Remarks', '转账时间': 'Transfer Time', '支付链接': 'Payment URL', '二维码图片': 'QR Code Image', '授权ID (AUTHORIZATION_ID时有值)': 'Authorization ID', '授权Token (AUTHORIZATION_ID时有值)': 'Authorization Token', '前端授权Token (AUTHORIZATION_ID时有值)': 'App Authorization Token', 'Payer 信息': 'Payer Info', '支付信息': 'Payment Info' };
    return map[desc] || desc;
  };

  const webhookTab = (
    <div style={{ maxWidth: 1600, margin: '0 auto', lineHeight: 1.8, fontSize: 13.5, color: COLORS.textPrimary }}>
      {/* Title */}
      <div style={{ padding: '20px 24px', background: 'linear-gradient(135deg, #fff1f0 0%, #fff7e6 100%)', borderRadius: 8, marginBottom: 20, borderLeft: '3px solid #eb2f96' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#1a1a1a', marginBottom: 4 }}>🔔 {t('doc_webhookTitle')}</div>
        <div style={{ fontSize: 13, color: '#666' }}>{t('doc_webhookSubtitle')}</div>
      </div>

      {/* Security & Retry */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: '16px 20px', height: '100%', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#fff1f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🔒</span>
              {t('doc_webhookSecurity')}
            </div>
            {[t('doc_webhookSecurityDesc1'), t('doc_webhookSecurityDesc2'), t('doc_webhookSecurityDesc3')].map((item, i) => (
              <div key={i} style={{ fontSize: 12, padding: '4px 0', display: 'flex', alignItems: 'center', gap: 8, color: COLORS.textSecondary }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#eb2f96', flexShrink: 0 }} /> {item}
              </div>
            ))}
          </div>
        </Col>
        <Col span={12}>
          <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: '16px 20px', height: '100%', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#fff7e6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🔄</span>
              {t('doc_webhookRetry')}
            </div>
            <div style={{ fontSize: 12, color: COLORS.textSecondary, marginBottom: 8 }}>{t('doc_webhookRetryDesc')}</div>
            <div style={{ padding: '8px 12px', background: '#fffbe6', borderRadius: 6, fontSize: 12, color: '#ad6800', border: '1px solid #ffe58f' }}>
              ⚠️ {t('doc_webhookRetryWarning')}
            </div>
          </div>
        </Col>
      </Row>

      {/* Verify & Headers */}
      <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, marginBottom: 16, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 20px', borderBottom: `1px solid ${COLORS.borderLight}`, background: '#fafbfc' }}>
          <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#f0f5ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>🔑</span>
          <span style={{ fontWeight: 600, fontSize: 14 }}>{t('doc_webhookVerifyTitle')}</span>
        </div>
        <div style={{ padding: '16px 20px' }}>
          <p style={{ margin: '0 0 8px', color: COLORS.textSecondary }}>{t('doc_webhookVerifyDesc')}</p>
          <p style={{ margin: '0 0 8px', color: COLORS.textSecondary }}>{t('doc_webhookHeaderDesc')}</p>
          <pre style={{ background: COLORS.codeBg, color: COLORS.textCode, padding: '14px 18px', borderRadius: 6, fontSize: 12, fontFamily: "'JetBrains Mono', 'SF Mono', monospace", lineHeight: 1.7, margin: '8px 0 10px', border: 'none' }}>
{`X-SIGNATURE: base64_ecc_signature
X-TIMESTAMP: 1700000000`}
          </pre>
          <div style={{ padding: '8px 12px', background: '#fff2f0', borderRadius: 6, fontSize: 12, color: '#cf1322', border: '1px solid #ffccc7' }}>
            🔒 {t('doc_webhookVerifyWarning')}
          </div>
        </div>
      </div>

      {/* Request Body */}
      <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, marginBottom: 16, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 20px', borderBottom: `1px solid ${COLORS.borderLight}`, background: '#fafbfc' }}>
          <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#e6fffb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>📨</span>
          <span style={{ fontWeight: 600, fontSize: 14 }}>{t('doc_webhookRequestBody')}</span>
        </div>
        <div style={{ padding: '16px 20px' }}>
          <Table size="small" pagination={false} bordered dataSource={[
            { name: 'msgId', type: 'String', required: true, desc: t('doc_webhookFieldMsgId') },
            { name: 'eventType', type: 'String', required: true, desc: t('doc_webhookFieldEventType') },
            { name: 'version', type: 'String', required: true, desc: t('doc_webhookFieldVersion') },
            { name: 'data', type: 'JsonString', required: true, desc: t('doc_webhookFieldData') },
          ]} columns={[
            { title: t('doc_webhookFieldName'), dataIndex: 'name', width: 120, render: v => <Text code>{v}</Text> },
            { title: t('doc_webhookFieldType'), dataIndex: 'type', width: 100, render: v => <Tag color="processing">{v}</Tag> },
            { title: t('doc_webhookFieldRequired'), dataIndex: 'required', width: 70, align: 'center', render: v => v ? <Tag color="red">Y</Tag> : <Tag>N</Tag> },
            { title: t('doc_webhookFieldDesc'), dataIndex: 'desc' },
          ]} rowKey="name" />

          <div style={{ marginTop: 14 }}>
            <Text strong>{t('doc_webhookExample')}:</Text>
            <pre style={{ background: COLORS.codeBg, color: COLORS.textCode, padding: '14px 18px', borderRadius: 6, fontSize: 12, fontFamily: "'JetBrains Mono', 'SF Mono', monospace", lineHeight: 1.7, margin: '8px 0 0', border: 'none' }}>
{`{
  "msgId": "msg_20260320_001",
  "eventType": "DEPOSIT",
  "version": "1.0.0",
  "data": "{\\"orderId\\":\\"ORD20260320001\\",\\"status\\":\\"SUCCESS\\",...}"
}`}
            </pre>
          </div>
        </div>
      </div>

      {/* Event Type Data Structures */}
      <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, marginBottom: 16, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: `1px solid ${COLORS.borderLight}`, background: '#fafbfc' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#f6ffed', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>📋</span>
            <span style={{ fontWeight: 600, fontSize: 14 }}>{t('doc_webhookDataStructure')}</span>
          </div>
          <Select
            value={webhookEventType}
            onChange={setWebhookEventType}
            style={{ width: 280 }}
            options={webhookEventOptions}
          />
        </div>
        <div style={{ padding: '16px 20px' }}>
          {/* Event type tags */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
            {webhookEventOptions.map(opt => (
              <Tag
                key={opt.value}
                color={webhookEventType === opt.value ? opt.color : undefined}
                style={{ cursor: 'pointer', borderStyle: webhookEventType === opt.value ? 'solid' : 'dashed' }}
                onClick={() => setWebhookEventType(opt.value)}
              >
                {opt.value}
              </Tag>
            ))}
          </div>

          <Table
            size="small"
            pagination={false}
            bordered
            dataSource={(WEBHOOK_EVENT_DATA[webhookEventType] || []).flatMap(item =>
              item.children ? [item, ...item.children.map(c => ({ ...c, _isChild: true }))] : [item]
            )}
            columns={[
              { title: t('doc_webhookFieldName'), dataIndex: 'name', width: 200, render: (v, r) => r._isChild
                ? <span style={{ paddingLeft: 16 }}><Text code style={{ fontSize: 11, color: '#8c8c8c' }}>{v.replace(/^\w+\./, '↳ ')}</Text></span>
                : <Text code>{v}</Text>
              },
              { title: t('doc_webhookFieldType'), dataIndex: 'type', width: 110, render: v => <Tag color="processing">{v}</Tag> },
              { title: t('doc_webhookFieldRequired'), dataIndex: 'required', width: 70, align: 'center', render: v => v ? <Tag color="red">Y</Tag> : <Tag>N</Tag> },
              { title: t('doc_webhookFieldDesc'), dataIndex: 'desc', render: v => <span style={{ fontSize: 12 }}>{localizeWebhookDesc(v)}</span> },
            ]}
            rowKey="name"
            rowClassName={r => r._isChild ? 'webhook-child-row' : ''}
          />
          <style>{`.webhook-child-row td { background: #fafbfc !important; }`}</style>
        </div>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════
  // GUIDE TAB — Clean Checkout.com-inspired cards
  // ═══════════════════════════════════════════════════════════
  const codeBlock = (text) => (
    <pre style={{ background: COLORS.codeBg, color: COLORS.textCode, padding: '14px 18px', borderRadius: 6, fontSize: 12, fontFamily: "'JetBrains Mono', 'SF Mono', monospace", lineHeight: 1.7, overflowX: 'auto', margin: '10px 0 14px', border: 'none' }}>{text}</pre>
  );
  const guideSection = (num, title, children) => (
    <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, marginBottom: 20, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 20px', borderBottom: `1px solid ${COLORS.borderLight}`, background: '#fafbfc' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '50%', background: COLORS.accent, color: '#fff', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{num}</span>
        <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.textPrimary }}>{title}</span>
      </div>
      <div style={{ padding: '16px 20px' }}>{children}</div>
    </div>
  );
  const guideAlert = (text, type = 'warning') => {
    const s = { warning: { bg: COLORS.orangeBg, border: '#fde68a', color: '#92400e', icon: '⚠️' }, danger: { bg: COLORS.redBg, border: '#fecaca', color: '#991b1b', icon: '🔒' }, info: { bg: COLORS.blueBg, border: COLORS.blueBorder, color: '#1e40af', icon: '💡' } }[type];
    return <div style={{ padding: '8px 12px', background: s.bg, border: `1px solid ${s.border}`, borderRadius: 6, fontSize: 12, color: s.color, marginTop: 10, lineHeight: 1.7 }}>{s.icon} {text}</div>;
  };

  const guideTab = (
    <div style={{ maxWidth: 1600, margin: '0 auto', lineHeight: 1.8, fontSize: 13.5, color: COLORS.textPrimary }}>
      <div style={{ padding: '16px 20px', background: COLORS.accentBg, borderRadius: 8, marginBottom: 24, fontSize: 13, color: COLORS.textSecondary, borderLeft: `3px solid ${COLORS.accent}` }}>
        {t('doc_guideIntro')}
      </div>

      {guideSection(1, t('doc_guideS1Title'), <>
        <Table size="small" pagination={false} bordered style={{ marginBottom: 10 }} dataSource={[
          { env: 'Sandbox', endpoint: 'https://sandbox-api.fiat.osl.com', endpointCn: 'https://sandbox-api.fiat.osl-pay.com' },
          { env: 'Production', endpoint: 'https://api.fiat.osl.com', endpointCn: 'https://api.fiat.osl-pay.com' },
        ]} columns={[
          { title: t('doc_guideS1Env'), dataIndex: 'env', width: 100, render: v => <Tag color={v === 'Sandbox' ? 'blue' : 'green'}>{v}</Tag> },
          { title: t('doc_guideS1Endpoint'), dataIndex: 'endpoint', render: v => <code style={{ fontSize: 12 }}>{v}</code> },
          { title: t('doc_guideS1CnLine'), dataIndex: 'endpointCn', render: v => <code style={{ fontSize: 12 }}>{v}</code> },
        ]} rowKey="env" />
        <div style={{ fontSize: 12, color: COLORS.textMuted }}>Content-Type: <code>application/json; charset=utf-8</code> | {t('doc_guideS1Timestamp')}</div>
        {guideAlert(t('doc_guideS1Whitelist'), 'warning')}
      </>)}

      {guideSection(2, t('doc_guideS2Title'), <>
        <p style={{ margin: '0 0 10px', color: COLORS.textSecondary }}>{t('doc_guideS2Desc')}</p>
        <Table size="small" pagination={false} bordered dataSource={[
          { header: 'Content-Type', desc: 'application/json', required: true },
          { header: 'appId', desc: t('doc_guideS2AppIdDesc'), required: true },
          { header: 'timestamp', desc: t('doc_guideS2TimestampDesc'), required: true },
          { header: 'signature', desc: t('doc_guideS2SignatureDesc'), required: true },
        ]} columns={[
          { title: 'Header', dataIndex: 'header', width: 140, render: v => <code style={{ fontWeight: 600, fontSize: 12 }}>{v}</code> },
          { title: t('doc_colDesc'), dataIndex: 'desc' },
          { title: t('doc_guideS2Required'), dataIndex: 'required', width: 70, align: 'center', render: v => v ? <span style={{ color: COLORS.red, fontWeight: 600, fontSize: 11 }}>required</span> : <span style={{ color: COLORS.textMuted, fontSize: 11 }}>optional</span> },
        ]} rowKey="header" />
        {guideAlert(t('doc_guideS2Warning'), 'danger')}
      </>)}

      {guideSection(3, t('doc_guideS3Title'), <>
        <p style={{ margin: '0 0 12px', color: COLORS.textSecondary }}>{t('doc_guideS3Desc')} <strong>{t('doc_guideS3ECC')}</strong> {t('doc_guideS3Desc2')} <code>SHA384withECDSA</code>{t('doc_guideS3Desc3')}</p>
        <Row gutter={16} style={{ marginBottom: 14 }}>
          <Col span={12}>
            <div style={{ padding: '12px 16px', background: COLORS.accentBg, borderRadius: 8, fontSize: 12, border: `1px solid ${COLORS.accent}22` }}>
              <strong style={{ fontSize: 13, color: COLORS.accent }}>🔐 {t('doc_guideS3MerchantKey')}</strong>
              <div style={{ marginTop: 6, color: COLORS.textSecondary }}>{t('doc_guideS3MerchantPriv')}</div>
              <div style={{ color: COLORS.textSecondary }}>{t('doc_guideS3MerchantPub')}</div>
            </div>
          </Col>
          <Col span={12}>
            <div style={{ padding: '12px 16px', background: COLORS.greenBg, borderRadius: 8, fontSize: 12, border: `1px solid ${COLORS.greenBorder}` }}>
              <strong style={{ fontSize: 13, color: COLORS.green }}>🔔 {t('doc_guideS3WebhookKey')}</strong>
              <div style={{ marginTop: 6, color: COLORS.textSecondary }}>{t('doc_guideS3WebhookPriv')}</div>
              <div style={{ color: COLORS.textSecondary }}>{t('doc_guideS3WebhookPub')}</div>
            </div>
          </Col>
        </Row>
        <strong>{t('doc_guideS3Plaintext')}</strong>
        <div style={{ marginTop: 6 }}>
          <Tag color="blue">GET</Tag>
          {codeBlock('appId=[appId]&method=GET&timestamp=[timestamp]&url=[path?querystring]')}
          <Tag color="green">POST</Tag>
          {codeBlock('appId=[appId]&method=POST&timestamp=[timestamp]&url=[path]&body=[bodyJsonString]')}
        </div>
        <strong>{t('doc_guideS3BodyRule')}</strong>
        <p style={{ fontSize: 12, color: COLORS.textSecondary, margin: '4px 0 6px' }}>{t('doc_guideS3BodyDesc')}</p>
        {codeBlock(`ObjectMapper mapper = new ObjectMapper();\nmapper.enable(SerializationFeature.ORDER_MAP_ENTRIES_BY_KEYS);\nmapper.setSerializationInclusion(JsonInclude.Include.NON_NULL);\nmapper.enable(MapperFeature.SORT_PROPERTIES_ALPHABETICALLY);\nmapper.configure(DeserializationFeature.FAIL_ON_UNKNOWN_PROPERTIES, false);\nmapper.registerModule(new JavaTimeModule());`)}
        <strong>{t('doc_guideS3KeyGen')}</strong>
        {codeBlock(`PRIV=$(openssl ecparam -genkey -name secp384r1 2>/dev/null)\necho "Private Key:"\necho "$PRIV" | openssl pkcs8 -topk8 -nocrypt -outform DER 2>/dev/null | base64 -w 0\necho ""\necho "Public Key:"\necho "$PRIV" | openssl ec -pubout -outform DER 2>/dev/null | base64 -w 0`)}
        <div style={{ fontSize: 12, color: COLORS.textMuted }}>{t('doc_guideS3KeyNote')}</div>
      </>)}

      {guideSection(4, t('doc_guideS4Title'), <>
        <p style={{ margin: '0 0 10px', color: COLORS.textSecondary }}>{t('doc_guideS4Desc')} <strong>{t('doc_guideS4Two')}</strong> {t('doc_guideS4Desc2')}</p>
        <Table size="small" pagination={false} bordered dataSource={[
          { rule: t('doc_guideS4NoSci'), example: '1e3', ok: false },
          { rule: t('doc_guideS4NoComma'), example: '1,000.00', ok: false },
          { rule: t('doc_guideS4UseDot'), example: '1000.50', ok: true },
          { rule: t('doc_guideS4NoTrail'), example: '"1000.50 "', ok: false },
          { rule: t('doc_guideS4NoExtra'), example: t('doc_guideS4ExceedPrecision'), ok: false },
        ]} columns={[
          { title: t('doc_guideS4Rule'), dataIndex: 'rule', width: 200 },
          { title: t('doc_example'), dataIndex: 'example', width: 160 },
          { title: '✓/✗', dataIndex: 'ok', width: 60, align: 'center', render: v => v ? <Tag color="success">✓</Tag> : <Tag color="error">✗</Tag> },
        ]} rowKey="rule" />
      </>)}

      {guideSection(5, t('doc_guideS5Title'), <>
        {codeBlock(`{\n  "code": 200,\n  "subCode": "000000",\n  "msg": "success",\n  "requestTime": 1686015475000,\n  "requestId": "requestIdxxxxx",\n  "data": {}\n}`)}
        <Table size="small" pagination={false} bordered dataSource={[
          { field: 'code', type: 'Integer', desc: t('doc_guideS5CodeDesc') },
          { field: 'subCode', type: 'String', desc: t('doc_guideS5SubCodeDesc') },
          { field: 'msg', type: 'String', desc: t('doc_guideS5MsgDesc') },
          { field: 'requestTime', type: 'Long', desc: t('doc_guideS5TimeDesc') },
          { field: 'requestId', type: 'String', desc: t('doc_guideS5ReqIdDesc') },
          { field: 'data', type: 'Object', desc: t('doc_guideS5DataDesc') },
        ]} columns={[
          { title: t('doc_guideS5Field'), dataIndex: 'field', width: 120, render: v => <code style={{ fontSize: 12 }}>{v}</code> },
          { title: t('doc_guideS5Type'), dataIndex: 'type', width: 80, render: v => <span style={{ fontSize: 11, color: '#3b82f6', fontWeight: 500 }}>{v.toLowerCase()}</span> },
          { title: t('doc_guideS5Desc'), dataIndex: 'desc' },
        ]} rowKey="field" />
      </>)}

      {guideSection(6, t('doc_guideS6Title'), <>
        <Table size="small" pagination={false} bordered dataSource={[
          { code: '200 / 000000', desc: t('doc_guideS6Success'), action: t('doc_guideS6SuccessAction'), color: COLORS.green },
          { code: '400xxx', desc: t('doc_guideS6Client'), action: t('doc_guideS6ClientAction'), color: COLORS.orange },
          { code: '4002xx', desc: t('doc_guideS6Sign'), action: t('doc_guideS6SignAction'), color: COLORS.red },
          { code: '500xxx', desc: t('doc_guideS6System'), action: t('doc_guideS6SystemAction'), color: '#8b5cf6' },
        ]} columns={[
          { title: t('doc_guideS6ErrCode'), dataIndex: 'code', width: 140, render: (v, r) => <code style={{ color: r.color, fontWeight: 600, fontSize: 12 }}>{v}</code> },
          { title: t('doc_guideS6Desc'), dataIndex: 'desc' },
          { title: t('doc_guideS6Action'), dataIndex: 'action', width: 200 },
        ]} rowKey="code" />
      </>)}

      {guideSection(7, t('doc_guideS7Title'), <>
        <p style={{ margin: '0 0 10px', color: COLORS.textSecondary }}>{t('doc_guideS7Desc')}</p>
        {codeBlock('X-SIGNATURE: base64_ecc_signature\nX-TIMESTAMP: 1700000000')}
        <p style={{ margin: '8px 0 6px', color: COLORS.textSecondary }}>{t('doc_guideS7Verify')}</p>
        {guideAlert(t('doc_guideS7Warning'), 'warning')}
      </>)}

      {guideSection(8, t('doc_guideS8Title'), <>
        <Table size="small" pagination={false} bordered dataSource={t('doc_guideS8Items')} columns={[
          { title: t('doc_guideS8CheckItem'), dataIndex: 'item', width: 380 },
          { title: t('doc_guideS8Cat'), dataIndex: 'cat', width: 90, render: v => <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 10, background: COLORS.accentBg, color: COLORS.accent, fontWeight: 500 }}>{v}</span> },
          { title: 'Sandbox', width: 80, align: 'center', render: () => <span style={{ display: 'inline-block', width: 16, height: 16, border: `2px solid ${COLORS.border}`, borderRadius: 3, background: '#fff' }} /> },
          { title: 'Prod', width: 80, align: 'center', render: () => <span style={{ display: 'inline-block', width: 16, height: 16, border: `2px solid ${COLORS.border}`, borderRadius: 3, background: '#fff' }} /> },
        ]} rowKey="item" />
      </>)}

      {guideSection(9, t('doc_guideS9Title'), <>
        <div style={{ position: 'relative', paddingLeft: 28 }}>
          <div style={{ position: 'absolute', left: 11, top: 8, bottom: 8, width: 2, background: `linear-gradient(180deg, ${COLORS.accent} 0%, ${COLORS.accentLight} 100%)`, borderRadius: 2 }} />
          {t('doc_guideS9Steps').map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 16, position: 'relative' }}>
              <div style={{ width: 22, height: 22, borderRadius: '50%', background: COLORS.accent, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, fontWeight: 700, position: 'relative', zIndex: 1 }}>{i + 1}</div>
              <div style={{ background: '#f8fafc', borderRadius: 6, padding: '8px 12px', flex: 1, border: `1px solid ${COLORS.border}` }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.textPrimary }}>{item.step}</div>
                <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </>)}

      {guideSection(10, t('doc_guideS10Title'), <>
        <Row gutter={16}>
          <Col span={12}>
            <div style={{ padding: '14px 18px', background: COLORS.blueBg, borderRadius: 8, border: `1px solid ${COLORS.blueBorder}` }}>
              <strong style={{ fontSize: 13, display: 'block', marginBottom: 10, color: '#1e40af' }}>🏢 {t('doc_guideS10FiatProvides')}</strong>
              {t('doc_guideS10FiatItems').map((item, i) => (
                <div key={i} style={{ fontSize: 12, padding: '3px 0', display: 'flex', alignItems: 'center', gap: 8, color: COLORS.textSecondary }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: COLORS.blue, flexShrink: 0 }} /> {item}
                </div>
              ))}
            </div>
          </Col>
          <Col span={12}>
            <div style={{ padding: '14px 18px', background: COLORS.greenBg, borderRadius: 8, border: `1px solid ${COLORS.greenBorder}` }}>
              <strong style={{ fontSize: 13, display: 'block', marginBottom: 10, color: '#166534' }}>💼 {t('doc_guideS10BizProvides')}</strong>
              {t('doc_guideS10BizItems').map((item, i) => (
                <div key={i} style={{ fontSize: 12, padding: '3px 0', display: 'flex', alignItems: 'center', gap: 8, color: COLORS.textSecondary }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: COLORS.green, flexShrink: 0 }} /> {item}
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </>)}
    </div>
  );

  // ═══════════════════════════════════════════════════════════
  // ADMIN PANEL
  // ═══════════════════════════════════════════════════════════
  const AdminPanel = () => {
    const [adminData, setAdminData] = useState([]);
    const [adminLoading, setAdminLoading] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [editForm] = Form.useForm();

    const loadAdminData = async () => {
      setAdminLoading(true);
      try { const { data } = await metaApi.adminList(); setAdminData(data || []); }
      catch (e) { message.error(t('doc_loadFailed')); }
      finally { setAdminLoading(false); }
    };
    useEffect(() => { loadAdminData(); }, []);

    const handleSave = async () => {
      try {
        const values = editForm.getFieldsValue();
        JSON.parse(values.fieldsJson);
        if (values.exampleJson) JSON.parse(values.exampleJson);
        if (editRecord?.id) { await metaApi.adminUpdate(editRecord.id, values); message.success(t('doc_updateSuccess')); }
        else { await metaApi.adminCreate(values); message.success(t('doc_createSuccess')); }
        setEditModal(false); loadAdminData(); loadOverview();
      } catch (e) {
        if (e instanceof SyntaxError) message.error(t('doc_jsonError'));
        else message.error(t('doc_saveFailed') + ': ' + (e.response?.data?.message || e.message));
      }
    };
    const handleDelete = async (id) => {
      try { await metaApi.adminDelete(id); message.success(t('doc_deleteSuccess')); loadAdminData(); loadOverview(); }
      catch (e) { message.error(t('doc_deleteFailed')); }
    };
    const handleRefresh = async () => {
      try { await metaApi.adminRefresh(); message.success(t('doc_cacheRefreshed')); loadOverview(); }
      catch (e) { message.error(t('doc_refreshFailed')); }
    };
    const handleCopy = (record) => {
      setEditRecord(null);
      editForm.setFieldsValue({ apiCode: record.apiCode, channelCode: record.channelCode + '_COPY', apiName: record.apiName + ' (Copy)', description: record.description, fieldsJson: record.fieldsJson, exampleJson: record.exampleJson });
      setEditModal(true);
    };

    return (
      <div style={{ maxWidth: 1600, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditRecord(null); editForm.resetFields(); setEditModal(true); }}>{t('doc_addConfig')}</Button>
          <Button icon={<ReloadOutlined />} onClick={handleRefresh}>{t('doc_refreshCache')}</Button>
          <span style={{ fontSize: 12, color: COLORS.textMuted }}>{t('doc_totalConfigs', adminData.length)}</span>
        </div>
        <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
          <Table
            columns={[
              { title: 'ID', dataIndex: 'id', width: 60 },
              { title: 'API Code', dataIndex: 'apiCode', width: 140, render: v => <Tag color={API_CODE_LABELS[v]?.color}>{v}</Tag> },
              { title: t('doc_adminChannel'), dataIndex: 'channelCode', width: 140 },
              { title: t('doc_apiNameLabel'), dataIndex: 'apiName', width: 160, ellipsis: true },
              { title: t('doc_adminStatus'), dataIndex: 'status', width: 80, render: v => <Tag color={v === 'ACTIVE' ? 'green' : 'default'}>{v}</Tag> },
              { title: t('doc_adminVersion'), dataIndex: 'version', width: 60 },
              { title: t('doc_adminCreator'), dataIndex: 'createdBy', width: 100 },
              { title: t('doc_adminAction'), key: 'action', width: 180, fixed: 'right', render: (_, record) => (
                <Space size={4}>
                  <Button type="link" size="small" icon={<EditOutlined />} onClick={() => { setEditRecord(record); editForm.setFieldsValue(record); setEditModal(true); }}>{t('doc_adminEdit')}</Button>
                  <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => handleCopy(record)}>{t('doc_adminCopy')}</Button>
                  <Popconfirm title={t('doc_confirmDelete')} onConfirm={() => handleDelete(record.id)}>
                    <Button type="link" size="small" danger icon={<DeleteOutlined />}>{t('doc_adminDelete')}</Button>
                  </Popconfirm>
                </Space>
              ) },
            ]}
            dataSource={adminData} rowKey="id" size="small" loading={adminLoading} scroll={{ x: 920 }}
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: n => t('doc_totalPagination', n) }}
          />
        </div>
        <Modal title={editRecord?.id ? t('doc_editMeta') : t('doc_addMeta')} open={editModal} onOk={handleSave} onCancel={() => setEditModal(false)} width={800} okText={t('save')} cancelText={t('cancel')}>
          <Form form={editForm} layout="vertical" size="small">
            <Row gutter={16}>
              <Col span={12}><Form.Item name="apiCode" label="API Code" rules={[{ required: true }]}><Select options={['ACCOUNT_CREATE', 'CARD_BIND', 'DEPOSIT_CREATE', 'WITHDRAW_CREATE', 'REFUND_CREATE', 'USER_CREATE'].map(v => ({ value: v, label: v }))} /></Form.Item></Col>
              <Col span={12}><Form.Item name="channelCode" label={t('doc_channelCodeLabel')} rules={[{ required: true }]}><Input placeholder="e.g. TAZAPAY" /></Form.Item></Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}><Form.Item name="apiName" label={t('doc_apiNameLabel')}><Input /></Form.Item></Col>
              <Col span={12}><Form.Item name="description" label={t('doc_descLabel')}><Input /></Form.Item></Col>
            </Row>
            <Form.Item name="fieldsJson" label={t('doc_fieldConfig')} rules={[{ required: true }]} extra={t('doc_fieldConfigExtra')}>
              <JsonEditor rows={14} placeholder='[{"name":"fieldName","label":"Label","type":"STRING","required":true,"group":"extParam"}]' />
            </Form.Item>
            <Form.Item name="exampleJson" label={t('doc_exampleRequestJson')}>
              <JsonEditor rows={6} placeholder='{"channelCode":"TAZAPAY","key":"value"}' />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════
  // FINAL LAYOUT — Admin Console style: gradient header + sidebar + content card
  // ═══════════════════════════════════════════════════════════
  const sideMenuItems = [
    { key: 'overview', icon: <FileTextOutlined />, label: t('doc_tabOverview') },
    { key: 'detail', icon: <ApiOutlined />, label: t('doc_tabDetail') },
    { key: 'webhook', icon: <BellOutlined />, label: t('doc_tabWebhook') },
    { key: 'guide', icon: <BookOutlined />, label: t('doc_tabGuide') },
    { key: 'matrix', icon: <BarChartOutlined />, label: t('doc_tabMatrix') },
    ...(isAdmin ? [{ key: 'admin', icon: <SettingOutlined />, label: t('doc_tabAdmin') }] : []),
  ];

  const sectionContent = {
    overview: overviewTab,
    detail: detailTab,
    webhook: webhookTab,
    guide: guideTab,
    matrix: <div style={{ maxWidth: 1600, margin: '0 auto' }}>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: COLORS.textPrimary, margin: '0 0 6px', letterSpacing: '-0.3px' }}>{t('doc_tabMatrix')}</h1>
          <p style={{ fontSize: 13, color: COLORS.textSecondary, margin: 0 }}>{t('doc_matrixSubtitle')}</p>
        </div>
        <ChannelCapabilityMatrix t={t} />
      </div>,
    admin: isAdmin ? <AdminPanel /> : null,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 64px)' }}>
      {/* ── Top gradient header bar (Admin Console style) ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 28px', height: 48,
        background: COLORS.accentGradient,
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <ApiOutlined style={{ color: '#fff', fontSize: 16 }} />
          <span style={{ color: '#fff', fontSize: 15, fontWeight: 700, letterSpacing: 0.3 }}>Fiat Payment API</span>
          <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, marginLeft: 2 }}>Reference v1</span>
        </div>
      </div>

      <div style={{ display: 'flex', flex: 1, background: COLORS.bgContent }}>
        {/* ── Left sidebar (Admin Console style) ── */}
        <div style={{
          width: 220, background: '#fff', flexShrink: 0,
          boxShadow: '2px 0 8px rgba(0,0,0,0.04)',
          overflow: 'auto',
        }}>
          <Menu
            mode="inline"
            selectedKeys={[activeSection]}
            onClick={({ key }) => setActiveSection(key)}
            items={sideMenuItems}
            className="apidoc-sidebar-menu"
            style={{ borderRight: 0, paddingTop: 12, fontSize: 13 }}
          />
        </div>

        {/* ── Main content (wrapped in white card like Admin Console) ── */}
        <div style={{ flex: 1, overflow: 'auto', minWidth: 0, padding: 24 }}>
          <div style={{
            background: '#fff', borderRadius: 10, padding: 24,
            minHeight: 'calc(100vh - 160px)',
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
          }}>
            {sectionContent[activeSection]}
          </div>
        </div>
      </div>

      {/* Admin Console sidebar menu styles */}
      <style>{`
        .apidoc-sidebar-menu .ant-menu-item {
          border-radius: 8px !important;
          margin: 2px 8px !important;
        }
        .apidoc-sidebar-menu .ant-menu-item-selected {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
          color: #fff !important;
        }
        .apidoc-sidebar-menu .ant-menu-item-selected .anticon {
          color: #fff !important;
        }
        .apidoc-sidebar-menu .ant-menu-item:not(.ant-menu-item-selected):hover {
          background: #f5f7fa !important;
        }
      `}</style>
    </div>
  );
}
